import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Utility Functions
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-IN');
  } catch (e) {
    return 'Invalid Date';
  }
};

const getStatusClass = (status) => {
  if (!status) return 'status-pending';
  const statusLower = status.toLowerCase();
  if (statusLower.includes('active duty') || statusLower.includes('active')) return 'status-active';
  if (statusLower.includes('cancel') || statusLower.includes('rejected')) return 'status-canceled';
  if (statusLower.includes('complete') || statusLower.includes('accepted')) return 'status-completed';
  if (statusLower.includes('pending') || statusLower.includes('self')) return 'status-pending';
  return 'status-pending';
};

const getRankClass = (rank) => {
  if (!rank) return 'rank-a';
  const rankStr = rank.toString().toUpperCase();
  if (rankStr.includes('A')) return 'rank-a';
  if (rankStr.includes('B')) return 'rank-b';
  if (rankStr.includes('C')) return 'rank-c';
  if (rankStr.includes('D')) return 'rank-d';
  return 'rank-a';
};

const exportToPDF = (data, title, columns, dataMapper) => {
  try {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text(title, 14, 22);
    doc.setFontSize(11);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    const tableData = data.map(dataMapper);

    autoTable(doc, {
      head: [columns],
      body: tableData,
      startY: 40,
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] }
    });

    doc.save(`${title.toLowerCase().replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (error) {
    console.error('PDF Export Error:', error);
    alert('Failed to export PDF. Please try again.');
  }
};

const exportToExcel = (data, title, dataMapper) => {
  try {
    const exportData = data.map(dataMapper);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${title.toLowerCase().replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  } catch (error) {
    console.error('Excel Export Error:', error);
    alert('Failed to export Excel. Please try again.');
  }
};

// API Configuration
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_BASE_URL || 'http://localhost:8080',
  ENDPOINTS: {
    ASSIGNMENTS: '/api/assignments/getall'
  },
  getHeaders: () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('adminToken') || localStorage.getItem('userToken')}`
  })
};

// Main App Component
export default function AdminUserVipHistory() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    assignments: [],
    replacedOfficers: []
  });
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedReplacement, setSelectedReplacement] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ASSIGNMENTS}`,
        {
          headers: API_CONFIG.getHeaders(),
          method: 'GET'
        }
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const apiData = await response.json();
      
      const normalizedData = {
        assignments: Array.isArray(apiData.assignments) ? apiData.assignments : [],
        replacedOfficers: Array.isArray(apiData.replacedOfficers) ? apiData.replacedOfficers : []
      };
      
      setData(normalizedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to load data: ${err.message}. Please check the backend server.`);
      setData({
        assignments: [],
        replacedOfficers: []
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 30000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const stats = useMemo(() => {
    const assignments = data.assignments || [];
    const replacedOfficers = data.replacedOfficers || [];

    let activeDuty = 0;
    let canceled = 0;
    let activeGuards = 0;
    let activeVips = 0;

    assignments.forEach(assignment => {
      if (assignment.status === 'Active Duty' || assignment.status === 'Active') activeDuty++;
      if (assignment.status === 'Canceled' || assignment.status === 'Rejected') canceled++;

      if (assignment.category && (assignment.category.status === 'Active' || assignment.category.status === 'Inactive')) {
        activeVips++;
      }

      if (assignment.officers) {
        assignment.officers.forEach(officer => {
          if (officer.status && officer.status.toLowerCase() === 'active') {
            activeGuards++;
          }
        });
      }
    });

    return {
      totalAssignments: assignments.length,
      activeDuty: activeDuty,
      canceled: canceled,
      activeGuards: activeGuards,
      activeVips: activeVips,
      replacements: replacedOfficers.length
    };
  }, [data]);

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const renderView = () => {
    if (loading) {
      return (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading data from API...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-message">
          <div className="error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={fetchData}>
            <i className="fas fa-sync-alt"></i> Retry
          </button>
        </div>
      );
    }

    switch (currentView) {
      case 'dashboard':
        return <DashboardView stats={stats} data={data} onViewDetails={setSelectedAssignment} />;
      case 'vip':
        return <VIPView data={data} onViewDetails={setSelectedAssignment} />;
      case 'guard':
        return <GuardView data={data} onViewDetails={setSelectedAssignment} />;
      case 'replacements':
        return <ReplacementsView data={data} onViewDetails={setSelectedReplacement} />;
      case 'reports':
        return <ReportsView data={data} />;
      default:
        return <DashboardView stats={stats} data={data} onViewDetails={setSelectedAssignment} />;
    }
  };

  return (
    <>
      <div className="admin-history-wrapper">
        <header className="page-header">
          <div className="header-content">
            <h1 className="page-title">
              <i className="fas fa-shield-alt"></i>
              Vip/Officer Assignments History
            </h1>
          </div>
        </header>

        <div className="tab-nav">
          <button
            className={`tab-button ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleViewChange('dashboard')}
          >
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
          <button
            className={`tab-button ${currentView === 'vip' ? 'active' : ''}`}
            onClick={() => handleViewChange('vip')}
          >
            <i className="fas fa-user-tie"></i>
            <span>VIP Assignments</span>
          </button>
          <button
            className={`tab-button ${currentView === 'guard' ? 'active' : ''}`}
            onClick={() => handleViewChange('guard')}
          >
            <i className="fas fa-user-shield"></i>
            <span>Officer Assignments</span>
          </button>
          <button
            className={`tab-button ${currentView === 'replacements' ? 'active' : ''}`}
            onClick={() => handleViewChange('replacements')}
          >
            <i className="fas fa-exchange-alt"></i>
            <span>Replacements</span>
          </button>
          <button
            className={`tab-button ${currentView === 'reports' ? 'active' : ''}`}
            onClick={() => handleViewChange('reports')}
          >
            <i className="fas fa-chart-bar"></i>
            <span>Reports</span>
          </button>
        </div>

        <div className="content-area">
          {renderView()}
        </div>

        {selectedAssignment && (
          <AssignmentDetailsModal
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
          />
        )}

        {selectedReplacement && (
          <ReplacementDetailsModal
            replacement={selectedReplacement}
            onClose={() => setSelectedReplacement(null)}
          />
        )}
      </div>

      <style jsx>{`
        .admin-history-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 20px;
        }

        .page-header {
          margin-bottom: 30px;
        }

        .header-content {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .page-title {
          font-size: 2.2rem;
          font-weight: 700;
          color: #60a5fa;
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .page-title i {
          font-size: 2.5rem;
          color: #3b82f6;
        }

        .connection-status {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(30, 41, 59, 0.8);
          padding: 12px 20px;
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.3);
          font-size: 0.9rem;
          color: #94a3b8;
          max-width: fit-content;
        }

        .connection-status i {
          color: #10b981;
        }

        .tab-nav {
          display: flex;
          background: rgba(15, 23, 42, 0.6);
          border-radius: 16px;
          padding: 8px;
          border: 1px solid rgba(59, 130, 246, 0.4);
          margin-bottom: 30px;
          overflow-x: auto;
          scrollbar-width: none;
        }

        .tab-nav::-webkit-scrollbar {
          display: none;
        }

        .tab-button {
          flex: 1;
          min-width: 140px;
          padding: 16px 20px;
          border: none;
          background: transparent;
          color: #94a3b8;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          border-radius: 12px;
          white-space: nowrap;
        }

        .tab-button:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .tab-button.active {
          background: #3b82f6;
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .tab-button i {
          font-size: 1.1rem;
        }

        .tab-button span {
          font-size: 0.95rem;
        }

        .content-area {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
          min-height: 500px;
        }

        .loading {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .loading .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #1e293b;
          border-top: 4px solid #60a5fa;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-message {
          text-align: center;
          padding: 60px 20px;
          max-width: 600px;
          margin: 0 auto;
        }

        .error-icon {
          font-size: 4rem;
          color: #ef4444;
          margin-bottom: 20px;
        }

        .error-message h3 {
          font-size: 1.8rem;
          margin-bottom: 15px;
          color: white;
        }

        .error-message p {
          color: #94a3b8;
          margin-bottom: 25px;
          line-height: 1.6;
        }

        .retry-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .retry-btn:hover {
          background: #2563eb;
          transform: translateY(-2px);
        }

        @media (max-width: 768px) {
          .admin-history-wrapper {
            padding: 15px;
          }

          .page-title {
            font-size: 1.8rem;
          }

          .tab-button {
            min-width: 120px;
            padding: 14px 16px;
          }

          .tab-button span {
            display: none;
          }

          .tab-button i {
            font-size: 1.3rem;
          }

          .content-area {
            padding: 20px;
          }
        }

        @media (max-width: 480px) {
          .page-title {
            font-size: 1.5rem;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .connection-status {
            width: 100%;
            max-width: 100%;
          }

          .tab-nav {
            justify-content: flex-start;
          }

          .tab-button {
            min-width: 70px;
            padding: 12px;
          }
        }
      `}</style>
    </>
  );
}

// Dashboard Component
function DashboardView({ stats, data, onViewDetails }) {
  const [sortConfig, setSortConfig] = useState({
    key: 'assignedAt',
    direction: 'desc'
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'fas fa-sort';
    return sortConfig.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  };

  const recentAssignments = useMemo(() => {
    let assignments = data.assignments || [];

    return [...assignments].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'vipName':
          aValue = a.category ? (a.category.name || a.category.username || '') : '';
          bValue = b.category ? (b.category.name || b.category.username || '') : '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'assignedAt':
          aValue = new Date(a.assignedAt || 0);
          bValue = new Date(b.assignedAt || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    }).slice(0, 10);
  }, [data.assignments, sortConfig]);

  const exportAllAssignments = () => {
    exportToPDF(
      data.assignments,
      'All Assignments Report',
      ['ID', 'VIP Name', 'Designation', 'Status', 'Assigned At', 'Start At', 'End At'],
      (assignment) => [
        assignment.id || 'N/A',
        assignment.category ? (assignment.category.name || assignment.category.username || 'No VIP') : 'No VIP',
        assignment.category?.designation || 'N/A',
        assignment.status || 'N/A',
        formatDate(assignment.assignedAt),
        formatDate(assignment.startAt),
        formatDate(assignment.endAt)
      ]
    );
  };

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' }}>
            <i className="fas fa-clipboard-list"></i>
          </div>
          <div className="stat-number">{stats.totalAssignments}</div>
          <div className="stat-label">Total Assignments</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}>
            <i className="fas fa-user-tie"></i>
          </div>
          <div className="stat-number">{stats.activeVips}</div>
          <div className="stat-label">Active VIPs</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' }}>
            <i className="fas fa-user-shield"></i>
          </div>
          <div className="stat-number">{stats.activeGuards}</div>
          <div className="stat-label">Active Officers</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6' }}>
            <i className="fas fa-exchange-alt"></i>
          </div>
          <div className="stat-number">{stats.replacements}</div>
          <div className="stat-label">Replacements</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-number">{stats.activeDuty}</div>
          <div className="stat-label">Active Duty</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>
            <i className="fas fa-times-circle"></i>
          </div>
          <div className="stat-number">{stats.canceled}</div>
          <div className="stat-label">Canceled</div>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">Recent Assignments</h3>
          <div className="table-actions">
            <button className="export-btn pdf" onClick={exportAllAssignments}>
              <i className="fas fa-file-pdf"></i> Export All
            </button>
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')}>
                  <span>ID</span>
                  <i className={getSortIcon('id')}></i>
                </th>
                <th onClick={() => handleSort('vipName')}>
                  <span>VIP Name</span>
                  <i className={getSortIcon('vipName')}></i>
                </th>
                <th>Officers</th>
                <th onClick={() => handleSort('status')}>
                  <span>Status</span>
                  <i className={getSortIcon('status')}></i>
                </th>
                <th onClick={() => handleSort('assignedAt')}>
                  <span>Assigned At</span>
                  <i className={getSortIcon('assignedAt')}></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentAssignments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-table">
                    <div className="empty-state">
                      <i className="fas fa-clipboard-list"></i>
                      <p>No assignments found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                recentAssignments.map(assignment => {
                  const vip = assignment.category;
                  const officers = assignment.officers || [];
                  const officerNames = officers.length > 0
                    ? officers.map(o => o.name || o.username || 'Unknown').join(', ')
                    : 'No Officer';

                  return (
                    <tr key={assignment.id || Math.random()}>
                      <td className="id-cell">{assignment.id || 'N/A'}</td>
                      <td className="vip-cell">{vip ? (vip.name || vip.username || 'No VIP') : 'No VIP'}</td>
                      <td className="officers-cell">{officerNames}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(assignment.status)}`}>
                          {assignment.status || 'N/A'}
                        </span>
                      </td>
                      <td className="date-cell">{formatDateTime(assignment.assignedAt)}</td>
                      <td className="actions-cell">
                        <button 
                          className="view-btn"
                          onClick={() => onViewDetails(assignment)}
                        >
                          <i className="fas fa-eye"></i> View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          border-color: #3b82f6;
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.15);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 15px;
          font-size: 1.8rem;
        }

        .stat-number {
          font-size: 2.2rem;
          font-weight: 800;
          color: white;
          margin-bottom: 8px;
          line-height: 1;
        }

        .stat-label {
          color: #94a3b8;
          font-size: 0.95rem;
          font-weight: 500;
        }

        .table-container {
          background: rgba(15, 23, 42, 0.6);
          border-radius: 16px;
          border: 1px solid rgba(59, 130, 246, 0.3);
          overflow: hidden;
        }

        .table-header {
          padding: 20px 24px;
          border-bottom: 1px solid rgba(59, 130, 246, 0.2);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 15px;
        }

        .table-title {
          font-size: 1.4rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .table-actions {
          display: flex;
          gap: 12px;
        }

        .export-btn {
          padding: 10px 20px;
          border-radius: 10px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .export-btn.pdf {
          background: #ef4444;
          color: white;
        }

        .export-btn.pdf:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .table-responsive {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 900px;
        }

        .data-table th {
          padding: 16px 20px;
          text-align: left;
          font-weight: 600;
          color: #60a5fa;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          background: rgba(59, 130, 246, 0.15);
          cursor: pointer;
          user-select: none;
          border-bottom: 1px solid rgba(59, 130, 246, 0.2);
        }

        .data-table th:hover {
          background: rgba(59, 130, 246, 0.25);
        }

        .data-table th span {
          margin-right: 8px;
        }

        .data-table th i {
          font-size: 0.8rem;
          opacity: 0.7;
        }

        .data-table tr {
          transition: all 0.3s ease;
        }

        .data-table tr:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .data-table td {
          padding: 18px 20px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .id-cell {
          font-weight: 600;
          color: #60a5fa;
        }

        .vip-cell {
          font-weight: 600;
          color: white;
        }

        .officers-cell {
          color: #cbd5e1;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .date-cell {
          color: #94a3b8;
          font-size: 0.9rem;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.85rem;
          display: inline-block;
        }

        .status-badge.status-active {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .status-badge.status-canceled {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .status-badge.status-completed {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .status-badge.status-pending {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .actions-cell {
          min-width: 100px;
        }

        .view-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
        }

        .view-btn:hover {
          background: #2563eb;
          transform: translateY(-2px);
        }

        .empty-table {
          padding: 60px 20px;
        }

        .empty-state {
          text-align: center;
          color: #64748b;
        }

        .empty-state i {
          font-size: 3rem;
          margin-bottom: 15px;
          opacity: 0.5;
        }

        .empty-state p {
          font-size: 1.1rem;
          margin: 0;
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }

          .stat-card {
            padding: 20px;
          }

          .stat-icon {
            width: 50px;
            height: 50px;
            font-size: 1.5rem;
          }

          .stat-number {
            font-size: 1.8rem;
          }

          .table-header {
            padding: 15px 20px;
            flex-direction: column;
            align-items: stretch;
          }

          .table-actions {
            justify-content: center;
          }

          .data-table th,
          .data-table td {
            padding: 14px 16px;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .export-btn span {
            display: none;
          }

          .export-btn {
            padding: 10px 15px;
          }
        }
      `}</style>
    </>
  );
}

// VIP View Component
function VIPView({ data, onViewDetails }) {
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'assignedAt',
    direction: 'desc'
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'fas fa-sort';
    return sortConfig.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  };

  const filteredAssignments = useMemo(() => {
    return (data.assignments || []).filter(assignment => {
      const vip = assignment.category;
      const vipName = vip ? (vip.name || vip.username || '').toLowerCase() : '';
      const assignedDate = new Date(assignment.assignedAt || 0);

      if (filters.search && !vipName.includes(filters.search.toLowerCase())) {
        return false;
      }

      if (filters.status !== 'all' && assignment.status !== filters.status) {
        return false;
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        if (assignedDate < fromDate) return false;
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (assignedDate > toDate) return false;
      }

      return true;
    });
  }, [data.assignments, filters]);

  const sortedAssignments = useMemo(() => {
    return [...filteredAssignments].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'vipName':
          const aVip = a.category ? (a.category.name || a.category.username || '') : '';
          const bVip = b.category ? (b.category.name || b.category.username || '') : '';
          aValue = aVip;
          bValue = bVip;
          break;
        case 'designation':
          aValue = a.category?.designation || '';
          bValue = b.category?.designation || '';
          break;
        case 'assignedAt':
          aValue = new Date(a.assignedAt || 0);
          bValue = new Date(b.assignedAt || 0);
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredAssignments, sortConfig]);

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };


  const exportVIPPDF = () => {
    exportToPDF(
      sortedAssignments,
      'VIP Assignments Report',
      ['ID', 'VIP Name', 'Designation', 'Email', 'Contact', 'Status', 'Assigned At', 'Start At', 'End At'],
      (assignment) => [
        assignment.id || 'N/A',
        assignment.category ? (assignment.category.name || assignment.category.username || 'No VIP') : 'No VIP',
        assignment.category?.designation || 'N/A',
        assignment.category?.email || 'N/A',
        assignment.category?.contactno || 'N/A',
        assignment.status || 'N/A',
        formatDate(assignment.assignedAt),
        formatDate(assignment.startAt),
        formatDate(assignment.endAt)
      ]
    );
  };

  const exportVIPExcel = () => {
    exportToExcel(
      sortedAssignments,
      'VIP Assignments',
      (assignment) => ({
        'Assignment ID': assignment.id || 'N/A',
        'VIP Name': assignment.category ? (assignment.category.name || assignment.category.username || 'No VIP') : 'No VIP',
        'Designation': assignment.category?.designation || 'N/A',
        'Email': assignment.category?.email || 'N/A',
        'Contact': assignment.category?.contactno || 'N/A',
        'Status': assignment.status || 'N/A',
        'Times Assigned': assignment.timesAssigned || 0,
        'Assigned At': formatDateTime(assignment.assignedAt),
        'Start At': formatDateTime(assignment.startAt),
        'End At': formatDateTime(assignment.endAt),
        'Completed At': formatDateTime(assignment.atEnd)
      })
    );
  };

  return (
    <>
      <div className="filters-section">
        <div className="filter-group">
          <div className="filter-input">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search VIP name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="Active Duty">Active Duty</option>
            <option value="Active">Active</option>
            <option value="Canceled">Canceled</option>
            <option value="Completed">Completed</option>
          </select>

          <div className="date-filters">
            <div className="date-input">
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <span className="date-separator">to</span>
            <div className="date-input">
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
          </div>

          <button className="clear-filters" onClick={clearFilters}>
            <i className="fas fa-times"></i> Clear
          </button>
        </div>

        <div className="export-buttons">
          <button className="export-btn pdf" onClick={exportVIPPDF}>
            <i className="fas fa-file-pdf"></i> Export Filtered (PDF)
          </button>
          <button className="export-btn excel" onClick={exportVIPExcel}>
            <i className="fas fa-file-excel"></i> Export Filtered (Excel)
          </button>
          <button className="export-btn all" onClick={() => exportToPDF(
            data.assignments,
            'All VIP Assignments',
            ['ID', 'VIP Name', 'Designation', 'Email', 'Contact', 'Status', 'Assigned At'],
            (assignment) => [
              assignment.id || 'N/A',
              assignment.category ? (assignment.category.name || assignment.category.username || 'No VIP') : 'No VIP',
              assignment.category?.designation || 'N/A',
              assignment.category?.email || 'N/A',
              assignment.category?.contactno || 'N/A',
              assignment.status || 'N/A',
              formatDate(assignment.assignedAt)
            ]
          )}>
            <i className="fas fa-file-pdf"></i> Export All VIP
          </button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            VIP Assignment Details 
            <span className="record-count"> ({sortedAssignments.length} records)</span>
          </h3>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')}>
                  <span>ID</span>
                  <i className={getSortIcon('id')}></i>
                </th>
                <th onClick={() => handleSort('vipName')}>
                  <span>VIP Name</span>
                  <i className={getSortIcon('vipName')}></i>
                </th>
                <th onClick={() => handleSort('designation')}>
                  <span>Designation</span>
                  <i className={getSortIcon('designation')}></i>
                </th>
                <th>Email</th>
                <th onClick={() => handleSort('status')}>
                  <span>Status</span>
                  <i className={getSortIcon('status')}></i>
                </th>
                <th onClick={() => handleSort('assignedAt')}>
                  <span>Assigned At</span>
                  <i className={getSortIcon('assignedAt')}></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAssignments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-table">
                    <div className="empty-state">
                      <i className="fas fa-user-tie"></i>
                      <p>No VIP assignments found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedAssignments.map(assignment => {
                  const vip = assignment.category;

                  return (
                    <tr key={assignment.id || Math.random()}>
                      <td className="id-cell">{assignment.id || 'N/A'}</td>
                      <td className="vip-cell">
                        <div className="vip-info">
                          <div className="vip-name">{vip ? (vip.name || vip.username || 'No VIP') : 'No VIP'}</div>
                          {vip?.contactno && (
                            <div className="vip-contact">{vip.contactno}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="designation-badge">
                          {vip ? vip.designation : 'N/A'}
                        </span>
                      </td>
                      <td className="email-cell">{vip ? vip.email : 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(assignment.status)}`}>
                          {assignment.status || 'N/A'}
                        </span>
                      </td>
                      <td className="date-cell">{formatDateTime(assignment.assignedAt)}</td>
                      <td className="actions-cell">
                        <button 
                          className="details-btn"
                          onClick={() => onViewDetails(assignment)}
                        >
                          <i className="fas fa-eye"></i> Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
      /* Add these styles to your existing CSS */

.table-container {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.table-responsive {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 800px;
}

.data-table thead {
  background: rgba(30, 41, 59, 0.9);
}

.data-table th {
  padding: 16px 12px;
  text-align: left;
  color: #cbd5e1;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid rgba(59, 130, 246, 0.3);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.data-table th:hover {
  background: rgba(51, 65, 85, 0.8);
}

.data-table th span {
  margin-right: 8px;
}

.data-table th i {
  font-size: 0.8rem;
  opacity: 0.7;
}

.data-table td {
  padding: 16px 12px;
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  vertical-align: middle;
}

.data-table tbody tr {
  background: rgba(15, 23, 42, 0.3);
  transition: background-color 0.2s ease;
}

.data-table tbody tr:nth-child(even) {
  background: rgba(15, 23, 42, 0.2);
}

.data-table tbody tr:hover {
  background: rgba(30, 41, 59, 0.5);
}

.empty-table {
  text-align: center;
  padding: 60px !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 1.1rem;
}

/* Guard info specific styles */
.guard-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.guard-name {
  font-weight: 600;
  color: white;
}

.guard-email {
  font-size: 0.85rem;
  color: #94a3b8;
}

/* Rank badge styles */
.rank-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.85rem;
  display: inline-block;
  white-space: nowrap;
}

.rank-badge.rank-a {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.rank-badge.rank-b {
  background: rgba(139, 92, 246, 0.2);
  color: #8b5cf6;
}

.rank-badge.rank-c {
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

/* Status badge styles - ADD THESE */
.status-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.85rem;
  display: inline-block;
  white-space: nowrap;
}

.status-badge.active {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.status-badge.inactive {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.status-badge.assigned {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}

.status-badge.unassigned {
  background: rgba(148, 163, 184, 0.2);
  color: #94a3b8;
}

.status-badge.pending {
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

/* Cell styles */
.experience-cell {
  font-weight: 600;
  color: white;
}

.vip-cell {
  color: white;
  font-weight: 500;
}

.date-cell {
  color: #94a3b8;
  font-size: 0.9rem;
  white-space: nowrap;
}

.actions-cell {
  white-space: nowrap;
}

/* Responsive table styles */
@media (max-width: 768px) {
  .table-container {
    padding: 16px;
  }
  
  .table-title {
    font-size: 1.25rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 12px 8px;
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .data-table th span {
    display: block;
    margin-right: 0;
  }
  
  .data-table th i {
    display: none;
  }
}
        .filters-section {
          background: rgba(15, 23, 42, 0.6);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .filter-group {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .filter-input {
          flex: 1;
          min-width: 250px;
          position: relative;
        }

        .filter-input i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          z-index: 1;
        }

        .filter-input input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          background: rgba(30, 41, 59, 0.8);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
        }

        .filter-input input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }

        .filter-select {
          min-width: 160px;
          padding: 12px 16px;
          background: rgba(30, 41, 59, 0.8);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
          cursor: pointer;
        }

        .filter-select:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .date-filters {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 300px;
        }

        .date-input {
          flex: 1;
          position: relative;
        }

        .date-input i {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 0.9rem;
        }

        .date-input input {
          width: 100%;
          padding: 12px 16px 12px 36px;
          background: rgba(30, 41, 59, 0.8);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
        }

        .date-separator {
          color: #94a3b8;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .clear-filters {
          padding: 12px 20px;
          background: rgba(239, 68, 68, 0.2);
          border: 2px solid rgba(239, 68, 68, 0.4);
          border-radius: 12px;
          color: #ef4444;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .clear-filters:hover {
          background: rgba(239, 68, 68, 0.3);
          transform: translateY(-2px);
        }

        .export-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .export-btn {
          padding: 12px 20px;
          border-radius: 12px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
        }

        .export-btn.pdf {
          background: #ef4444;
          color: white;
        }

        .export-btn.pdf:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .export-btn.excel {
          background: #10b981;
          color: white;
        }

        .export-btn.excel:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .export-btn.all {
          background: #f59e0b;
          color: white;
        }

        .export-btn.all:hover {
          background: #d97706;
          transform: translateY(-2px);
        }

        .record-count {
          color: #94a3b8;
          font-weight: normal;
          margin-left: 8px;
        }

        .vip-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .vip-name {
          font-weight: 600;
          color: white;
        }

        .vip-contact {
          font-size: 0.85rem;
          color: #94a3b8;
        }

        .designation-badge {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          padding: 6px 12px;
          border-radius: 16px;
          font-weight: 600;
          font-size: 0.85rem;
          display: inline-block;
        }

        .email-cell {
          color: #cbd5e1;
          font-size: 0.9rem;
        }

        .details-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
        }

        .details-btn:hover {
          background: #2563eb;
          transform: translateY(-2px);
        }

        @media (max-width: 1024px) {
          .filter-group {
            flex-direction: column;
          }

          .filter-input,
          .filter-select,
          .date-filters {
            min-width: 100%;
          }

          .clear-filters {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .filters-section {
            padding: 16px;
          }

          .date-filters {
            flex-direction: column;
            gap: 12px;
          }

          .date-separator {
            display: none;
          }

          .export-buttons {
            flex-direction: column;
          }

          .export-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
}

// Guard View Component
function GuardView({ data, onViewDetails }) {
  const [filters, setFilters] = useState({
    search: '',
    rank: 'all',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'guardName',
    direction: 'asc'
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'fas fa-sort';
    return sortConfig.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  };

  const guardData = useMemo(() => {
    const guards = [];
    (data.assignments || []).forEach(assignment => {
      const officers = assignment.officers || [];
      const vip = assignment.category;

      officers.forEach(officer => {
        guards.push({
          assignmentId: assignment.id,
          officer: officer,
          assignmentStatus: assignment.status,
          assignedAt: assignment.assignedAt,
          startAt: assignment.startAt,
          endAt: assignment.endAt,
          vip: vip
        });
      });
    });
    return guards;
  }, [data.assignments]);

  const filteredGuards = useMemo(() => {
    return guardData.filter(item => {
      const officerName = (item.officer.name || item.officer.username || '').toLowerCase();
      const assignedDate = new Date(item.assignedAt || 0);

      if (filters.search && !officerName.includes(filters.search.toLowerCase())) {
        return false;
      }

      if (filters.rank !== 'all' && item.officer.rank !== filters.rank) {
        return false;
      }

      if (filters.status !== 'all' && item.officer.status.toLowerCase() !== filters.status.toLowerCase()) {
        return false;
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        if (assignedDate < fromDate) return false;
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (assignedDate > toDate) return false;
      }

      return true;
    });
  }, [guardData, filters]);

  const sortedGuards = useMemo(() => {
    return [...filteredGuards].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'guardName':
          aValue = a.officer.name || a.officer.username || '';
          bValue = b.officer.name || b.officer.username || '';
          break;
        case 'rank':
          aValue = a.officer.rank || '';
          bValue = b.officer.rank || '';
          break;
        case 'experience':
          aValue = a.officer.experience || 0;
          bValue = b.officer.experience || 0;
          break;
        case 'assignmentStatus':
          aValue = a.assignmentStatus || '';
          bValue = b.assignmentStatus || '';
          break;
        case 'assignedAt':
          aValue = new Date(a.assignedAt || 0);
          bValue = new Date(b.assignedAt || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredGuards, sortConfig]);

  const clearFilters = () => {
    setFilters({
      search: '',
      rank: 'all',
      status: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const exportGuardPDF = () => {
    exportToPDF(
      sortedGuards,
      'Guard Assignments Report',
      ['Assignment ID', 'Guard Name', 'Rank', 'Guard Status', 'Experience', 'Contact', 'VIP Name', 'Assignment Status', 'Assigned At'],
      (item) => [
        item.assignmentId || 'N/A',
        item.officer.name || item.officer.username || 'N/A',
        item.officer.rank || 'N/A',
        item.officer.status || 'N/A',
        item.officer.experience || 0,
        item.officer.contactno || 'N/A',
        item.vip ? (item.vip.name || item.vip.username || 'No VIP') : 'No VIP',
        item.assignmentStatus || 'N/A',
        formatDate(item.assignedAt)
      ]
    );
  };

  const exportGuardExcel = () => {
    exportToExcel(
      sortedGuards,
      'Guard Assignments',
      (item) => ({
        'Assignment ID': item.assignmentId || 'N/A',
        'Guard Name': item.officer.name || item.officer.username || 'N/A',
        'Rank': item.officer.rank || 'N/A',
        'Guard Status': item.officer.status || 'N/A',
        'Experience': item.officer.experience || 0,
        'Email': item.officer.email || 'N/A',
        'Contact': item.officer.contactno || 'N/A',
        'Aadhar': item.officer.adharNo || 'N/A',
        'Gender': item.officer.gender || 'N/A',
        'VIP Name': item.vip ? (item.vip.name || item.vip.username || 'No VIP') : 'No VIP',
        'Assignment Status': item.assignmentStatus || 'N/A',
        'Assigned At': formatDateTime(item.assignedAt),
        'Start At': formatDateTime(item.startAt),
        'End At': formatDateTime(item.endAt)
      })
    );
  };

  return (
    <>
      <div className="filters-section">
        <div className="filter-group">
          <div className="filter-input">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search officer..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          <select
            value={filters.rank}
            onChange={(e) => setFilters({ ...filters, rank: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Ranks</option>
            <option value="A Grade">A Grade</option>
            <option value="B Grade">B Grade</option>
            <option value="C Grade">C Grade</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <div className="date-filters">
            <div className="date-input">
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <span className="date-separator">to</span>
            <div className="date-input">
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
          </div>

          <button className="clear-filters" onClick={clearFilters}>
            <i className="fas fa-times"></i> Clear
          </button>
        </div>

        <div className="export-buttons">
          <button className="export-btn pdf" onClick={exportGuardPDF}>
            <i className="fas fa-file-pdf"></i> Export Filtered (PDF)
          </button>
          <button className="export-btn excel" onClick={exportGuardExcel}>
            <i className="fas fa-file-excel"></i> Export Filtered (Excel)
          </button>
          <button className="export-btn all" onClick={() => exportToPDF(
            guardData,
            'All Guard Assignments',
            ['Assignment ID', 'Guard Name', 'Rank', 'Status', 'VIP Name', 'Assigned At'],
            (item) => [
              item.assignmentId || 'N/A',
              item.officer.name || item.officer.username || 'N/A',
              item.officer.rank || 'N/A',
              item.officer.status || 'N/A',
              item.vip ? (item.vip.name || item.vip.username || 'No VIP') : 'No VIP',
              formatDate(item.assignedAt)
            ]
          )}>
            <i className="fas fa-file-pdf"></i> Export All Guards
          </button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            Officers Assignment Details 
            <span className="record-count"> ({sortedGuards.length} records)</span>
          </h3>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('guardName')}>
                  <span>Guard Name</span>
                  <i className={getSortIcon('guardName')}></i>
                </th>
                <th onClick={() => handleSort('rank')}>
                  <span>Rank</span>
                  <i className={getSortIcon('rank')}></i>
                </th>
                <th>Status</th>
                <th onClick={() => handleSort('experience')}>
                  <span>Experience</span>
                  <i className={getSortIcon('experience')}></i>
                </th>
                <th onClick={() => handleSort('assignmentStatus')}>
                  <span>Assignment Status</span>
                  <i className={getSortIcon('assignmentStatus')}></i>
                </th>
                <th>VIP Name</th>
                <th onClick={() => handleSort('assignedAt')}>
                  <span>Assigned At</span>
                  <i className={getSortIcon('assignedAt')}></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedGuards.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-table">
                    <div className="empty-state">
                      <i className="fas fa-user-shield"></i>
                      <p>No Officer assignments found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedGuards.map((item, index) => (
                  <tr key={`${item.assignmentId || index}-${item.officer.id || index}`}>
                    <td>
                      <div className="guard-info">
                        <div className="guard-name">{item.officer.name || item.officer.username || 'N/A'}</div>
                        <div className="guard-email">{item.officer.email || ''}</div>
                      </div>
                    </td>
                    <td>
                      <span className={`rank-badge ${getRankClass(item.officer.rank)}`}>
                        {item.officer.rank || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(item.officer.status)}`}>
                        {item.officer.status || 'N/A'}
                      </span>
                    </td>
                    <td className="experience-cell">{item.officer.experience || 0} years</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(item.assignmentStatus)}`}>
                        {item.assignmentStatus || 'N/A'}
                      </span>
                    </td>
                    <td className="vip-cell">{item.vip ? (item.vip.name || item.vip.username || 'No VIP') : 'No VIP'}</td>
                    <td className="date-cell">{formatDateTime(item.assignedAt)}</td>
                    <td className="actions-cell">
                      <button
                        className="details-btn"
                        onClick={() => {
                          const assignment = data.assignments.find(a => a.id === item.assignmentId);
                          if (assignment) onViewDetails(assignment);
                        }}
                      >
                        <i className="fas fa-eye"></i> Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
  .table-container {
    background: rgba(15, 23, 42, 0.6);
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid rgba(59, 130, 246, 0.3);
    overflow: hidden;
  }

  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .table-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
  }

  .record-count {
    color: #94a3b8;
    font-weight: normal;
    font-size: 1rem;
    margin-left: 8px;
  }

  .table-responsive {
    overflow-x: auto;
  }

  .data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 800px;
  }

  .data-table thead {
    background: rgba(30, 41, 59, 0.8);
  }

  .data-table th {
    padding: 16px 12px;
    text-align: left;
    color: #cbd5e1;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid rgba(59, 130, 246, 0.3);
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
  }

  .data-table th:hover {
    background: rgba(51, 65, 85, 0.8);
  }

  .data-table th span {
    margin-right: 8px;
  }

  .data-table th i {
    font-size: 0.8rem;
    opacity: 0.7;
  }

  .data-table td {
    padding: 16px 12px;
    border-bottom: 1px solid rgba(59, 130, 246, 0.1);
    vertical-align: middle;
  }

  .data-table tbody tr {
    transition: background-color 0.2s ease;
  }

  .data-table tbody tr:hover {
    background: rgba(30, 41, 59, 0.4);
  }

  .empty-table {
    text-align: center;
    padding: 60px !important;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: #94a3b8;
  }

  .empty-state i {
    font-size: 3rem;
    opacity: 0.5;
  }

  .empty-state p {
    margin: 0;
    font-size: 1.1rem;
  }

  /* Guard info styles */
  .guard-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .guard-name {
    font-weight: 600;
    color: white;
  }

  .guard-email {
    font-size: 0.85rem;
    color: #94a3b8;
  }

  /* Rank badge styles */
  .rank-badge {
    padding: 6px 12px;
    border-radius: 16px;
    font-weight: 600;
    font-size: 0.85rem;
    display: inline-block;
    white-space: nowrap;
  }

  .rank-badge.rank-a {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }

  .rank-badge.rank-b {
    background: rgba(139, 92, 246, 0.2);
    color: #8b5cf6;
  }

  .rank-badge.rank-c {
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
  }

  /* Status badge styles */
  .status-badge {
    padding: 6px 12px;
    border-radius: 16px;
    font-weight: 600;
    font-size: 0.85rem;
    display: inline-block;
    white-space: nowrap;
  }

  .status-badge.active {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
  }

  .status-badge.inactive {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }

  .status-badge.assigned {
    background: rgba(59, 130, 246, 0.2);
    color: #3b82f6;
  }

  .status-badge.unassigned {
    background: rgba(148, 163, 184, 0.2);
    color: #94a3b8;
  }

  .status-badge.pending {
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
  }

  /* Other cell styles */
  .experience-cell {
    font-weight: 600;
    color: white;
  }

  .vip-cell {
    color: white;
    font-weight: 500;
  }

  .date-cell {
    color: #94a3b8;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  /* Actions cell styles */
  .actions-cell {
    white-space: nowrap;
  }

  .details-btn {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
  }

  .details-btn:hover {
    background: #2563eb;
    transform: translateY(-2px);
  }

  /* Filters section styles */
  .filters-section {
    background: rgba(15, 23, 42, 0.6);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 24px;
    border: 1px solid rgba(59, 130, 246, 0.3);
  }

  .filter-group {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  .filter-input {
    flex: 1;
    min-width: 250px;
    position: relative;
  }

  .filter-input i {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    z-index: 1;
  }

  .filter-input input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    background: rgba(30, 41, 59, 0.8);
    border: 2px solid rgba(59, 130, 246, 0.4);
    border-radius: 12px;
    color: white;
    font-size: 0.95rem;
  }

  .filter-input input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  .filter-select {
    min-width: 160px;
    padding: 12px 16px;
    background: rgba(30, 41, 59, 0.8);
    border: 2px solid rgba(59, 130, 246, 0.4);
    border-radius: 12px;
    color: white;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .filter-select:focus {
    outline: none;
    border-color: #3b82f6;
  }

  .date-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 300px;
  }

  .date-input {
    flex: 1;
    position: relative;
  }

  .date-input i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.9rem;
  }

  .date-input input {
    width: 100%;
    padding: 12px 16px 12px 36px;
    background: rgba(30, 41, 59, 0.8);
    border: 2px solid rgba(59, 130, 246, 0.4);
    border-radius: 12px;
    color: white;
    font-size: 0.95rem;
  }

  .date-separator {
    color: #94a3b8;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .clear-filters {
    padding: 12px 20px;
    background: rgba(239, 68, 68, 0.2);
    border: 2px solid rgba(239, 68, 68, 0.4);
    border-radius: 12px;
    color: #ef4444;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .clear-filters:hover {
    background: rgba(239, 68, 68, 0.3);
    transform: translateY(-2px);
  }

  .export-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .export-btn {
    padding: 12px 20px;
    border-radius: 12px;
    border: none;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
  }

  .export-btn.pdf {
    background: #ef4444;
    color: white;
  }

  .export-btn.pdf:hover {
    background: #dc2626;
    transform: translateY(-2px);
  }

  .export-btn.excel {
    background: #10b981;
    color: white;
  }

  .export-btn.excel:hover {
    background: #059669;
    transform: translateY(-2px);
  }

  .export-btn.all {
    background: #f59e0b;
    color: white;
  }

  .export-btn.all:hover {
    background: #d97706;
    transform: translateY(-2px);
  }

  /* Responsive styles */
  @media (max-width: 1024px) {
    .filter-group {
      flex-direction: column;
    }

    .filter-input,
    .filter-select,
    .date-filters {
      min-width: 100%;
    }

    .clear-filters {
      width: 100%;
      justify-content: center;
    }
  }

  @media (max-width: 768px) {
    .table-container,
    .filters-section {
      padding: 16px;
    }

    .table-title {
      font-size: 1.25rem;
    }

    .date-filters {
      flex-direction: column;
      gap: 12px;
    }

    .date-separator {
      display: none;
    }

    .export-buttons {
      flex-direction: column;
    }

    .export-btn {
      width: 100%;
      justify-content: center;
    }

    .data-table th,
    .data-table td {
      padding: 12px 8px;
    }
  }

  @media (max-width: 480px) {
    .table-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .data-table th span {
      display: block;
      margin-right: 0;
    }

    .data-table th i {
      display: none;
    }
  }
`}</style>
    </>
  );
}

// Replacements View Component
function ReplacementsView({ data, onViewDetails }) {
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'id',
    direction: 'desc'
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'fas fa-sort';
    return sortConfig.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  };

  const replacements = data.replacedOfficers || [];

  const filteredReplacements = useMemo(() => {
    return replacements.filter(replacement => {
      const prevOfficer = replacement.previousOfficer ?
        (replacement.previousOfficer.name || replacement.previousOfficer.username || '').toLowerCase() : '';
      const currOfficer = replacement.currentOfficer ?
        (replacement.currentOfficer.name || replacement.currentOfficer.username || '').toLowerCase() : '';
      const replacedDate = new Date(replacement.replacedAt || replacement.createdAt || 0);

      if (filters.search && !prevOfficer.includes(filters.search.toLowerCase()) &&
        !currOfficer.includes(filters.search.toLowerCase())) {
        return false;
      }

      if (filters.status !== 'all' && replacement.status !== filters.status) {
        return false;
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        if (replacedDate < fromDate) return false;
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (replacedDate > toDate) return false;
      }

      return true;
    });
  }, [replacements, filters]);

  const sortedReplacements = useMemo(() => {
    return [...filteredReplacements].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'previousOfficer':
          aValue = a.previousOfficer ? (a.previousOfficer.name || a.previousOfficer.username || '') : '';
          bValue = b.previousOfficer ? (b.previousOfficer.name || b.previousOfficer.username || '') : '';
          break;
        case 'currentOfficer':
          aValue = a.currentOfficer ? (a.currentOfficer.name || a.currentOfficer.username || '') : '';
          bValue = b.currentOfficer ? (b.currentOfficer.name || b.currentOfficer.username || '') : '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'replacedAt':
          aValue = new Date(a.replacedAt || a.createdAt || 0);
          bValue = new Date(b.replacedAt || b.createdAt || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredReplacements, sortConfig]);

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const exportReplacementsPDF = () => {
    exportToPDF(
      sortedReplacements,
      'Replacements Report',
      ['ID', 'Assignment ID', 'Previous Officer', 'Previous Rank', 'Current Officer', 'Current Rank', 'Reason', 'Status', 'Accepted By', 'Replaced At'],
      (replacement) => [
        replacement.id || 'N/A',
        replacement.assignmentId || 'N/A',
        replacement.previousOfficer ? (replacement.previousOfficer.name || replacement.previousOfficer.username || 'N/A') : 'N/A',
        replacement.previousOfficer?.rank || 'N/A',
        replacement.currentOfficer ? (replacement.currentOfficer.name || replacement.currentOfficer.username || 'Not Assigned') : 'Not Assigned',
        replacement.currentOfficer?.rank || 'N/A',
        replacement.reason || replacement.reasonMessage || 'N/A',
        replacement.status || 'N/A',
        replacement.acceptedBy || 'N/A',
        formatDate(replacement.replacedAt || replacement.createdAt)
      ]
    );
  };

  const exportReplacementsExcel = () => {
    exportToExcel(
      sortedReplacements,
      'Replacements',
      (replacement) => ({
        'Replacement ID': replacement.id || 'N/A',
        'Assignment ID': replacement.assignmentId || 'N/A',
        'Previous Officer': replacement.previousOfficer ? (replacement.previousOfficer.name || replacement.previousOfficer.username || 'N/A') : 'N/A',
        'Previous Rank': replacement.previousOfficer?.rank || 'N/A',
        'Previous Status': replacement.previousOfficer?.status || 'N/A',
        'Current Officer': replacement.currentOfficer ? (replacement.currentOfficer.name || replacement.currentOfficer.username || 'Not Assigned') : 'Not Assigned',
        'Current Rank': replacement.currentOfficer?.rank || 'N/A',
        'Current Status': replacement.currentOfficer?.status || 'N/A',
        'Reason': replacement.reason || 'N/A',
        'Reason Message': replacement.reasonMessage || '',
        'Status': replacement.status || 'N/A',
        'Accepted By': replacement.acceptedBy || 'N/A',
        'Replaced At': formatDateTime(replacement.replacedAt || replacement.createdAt)
      })
    );
  };

  return (
    <>
      <div className="filters-section">
        <div className="filter-group">
          <div className="filter-input">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search Officer..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="Accepted">Accepted</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
          </select>

          <div className="date-filters">
            <div className="date-input">
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <span className="date-separator">to</span>
            <div className="date-input">
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
          </div>

          <button className="clear-filters" onClick={clearFilters}>
            <i className="fas fa-times"></i> Clear
          </button>
        </div>

        <div className="export-buttons">
          <button className="export-btn pdf" onClick={exportReplacementsPDF}>
            <i className="fas fa-file-pdf"></i> Export Filtered (PDF)
          </button>
          <button className="export-btn excel" onClick={exportReplacementsExcel}>
            <i className="fas fa-file-excel"></i> Export Filtered (Excel)
          </button>
          <button className="export-btn all" onClick={() => exportToPDF(
            data.replacedOfficers,
            'All Replacements',
            ['ID', 'Assignment ID', 'Previous Officer', 'Current Officer', 'Reason', 'Status', 'Replaced At'],
            (replacement) => [
              replacement.id || 'N/A',
              replacement.assignmentId || 'N/A',
              replacement.previousOfficer ? (replacement.previousOfficer.name || replacement.previousOfficer.username || 'N/A') : 'N/A',
              replacement.currentOfficer ? (replacement.currentOfficer.name || replacement.currentOfficer.username || 'Not Assigned') : 'Not Assigned',
              replacement.reason || replacement.reasonMessage || 'N/A',
              replacement.status || 'N/A',
              formatDate(replacement.replacedAt || replacement.createdAt)
            ]
          )}>
            <i className="fas fa-file-pdf"></i> Export All Replacements
          </button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            Replacement History 
            <span className="record-count"> ({sortedReplacements.length} records)</span>
          </h3>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')}>
                  <span>ID</span>
                  <i className={getSortIcon('id')}></i>
                </th>
                <th>Assignment ID</th>
                <th onClick={() => handleSort('previousOfficer')}>
                  <span>Previous Officer</span>
                  <i className={getSortIcon('previousOfficer')}></i>
                </th>
                <th onClick={() => handleSort('currentOfficer')}>
                  <span>Current Officer</span>
                  <i className={getSortIcon('currentOfficer')}></i>
                </th>
                <th>Reason</th>
                <th onClick={() => handleSort('status')}>
                  <span>Status</span>
                  <i className={getSortIcon('status')}></i>
                </th>
                <th onClick={() => handleSort('replacedAt')}>
                  <span>Replaced At</span>
                  <i className={getSortIcon('replacedAt')}></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedReplacements.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-table">
                    <div className="empty-state">
                      <i className="fas fa-exchange-alt"></i>
                      <p>No replacements found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedReplacements.map(replacement => (
                  <tr key={replacement.id || Math.random()}>
                    <td className="id-cell">{replacement.id || 'N/A'}</td>
                    <td>{replacement.assignmentId || 'N/A'}</td>
                    <td>
                      <div className="officer-info">
                        <div className="officer-name">
                          {replacement.previousOfficer ?
                            (replacement.previousOfficer.name || replacement.previousOfficer.username || 'N/A') : 'N/A'}
                        </div>
                        {replacement.previousOfficer?.rank && (
                          <div className="officer-rank">{replacement.previousOfficer.rank}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="officer-info">
                        <div className="officer-name">
                          {replacement.currentOfficer ?
                            (replacement.currentOfficer.name || replacement.currentOfficer.username || 'Not Assigned') : 'Not Assigned'}
                        </div>
                        {replacement.currentOfficer?.rank && (
                          <div className="officer-rank">{replacement.currentOfficer.rank}</div>
                        )}
                      </div>
                    </td>
                    <td className="reason-cell">
                      {replacement.reason || replacement.reasonMessage || 'N/A'}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(replacement.status)}`}>
                        {replacement.status || 'N/A'}
                      </span>
                    </td>
                    <td className="date-cell">{formatDateTime(replacement.replacedAt || replacement.createdAt)}</td>
                    <td className="actions-cell">
                      <button
                        className="details-btn"
                        onClick={() => onViewDetails(replacement)}
                      >
                        <i className="fas fa-info-circle"></i> Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
      
      .filters-section {
          background: rgba(15, 23, 42, 0.6);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .filter-group {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .filter-input {
          flex: 1;
          min-width: 250px;
          position: relative;
        }

        .filter-input i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          z-index: 1;
        }

        .filter-input input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          background: rgba(30, 41, 59, 0.8);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
        }

        .filter-input input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }

        .filter-select {
          min-width: 160px;
          padding: 12px 16px;
          background: rgba(30, 41, 59, 0.8);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
          cursor: pointer;
        }

        .filter-select:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .date-filters {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 300px;
        }

        .date-input {
          flex: 1;
          position: relative;
        }

        .date-input i {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 0.9rem;
        }

        .date-input input {
          width: 100%;
          padding: 12px 16px 12px 36px;
          background: rgba(30, 41, 59, 0.8);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
        }

        .date-separator {
          color: #94a3b8;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .clear-filters {
          padding: 12px 20px;
          background: rgba(239, 68, 68, 0.2);
          border: 2px solid rgba(239, 68, 68, 0.4);
          border-radius: 12px;
          color: #ef4444;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .clear-filters:hover {
          background: rgba(239, 68, 68, 0.3);
          transform: translateY(-2px);
        }

        .export-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .export-btn {
          padding: 12px 20px;
          border-radius: 12px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
        }

        .export-btn.pdf {
          background: #ef4444;
          color: white;
        }

        .export-btn.pdf:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .export-btn.excel {
          background: #10b981;
          color: white;
        }

        .export-btn.excel:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .export-btn.all {
          background: #f59e0b;
          color: white;
        }

        .export-btn.all:hover {
          background: #d97706;
          transform: translateY(-2px);
        }

        .record-count {
          color: #94a3b8;
          font-weight: normal;
          margin-left: 8px;
        }

        .officer-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
          
  .empty-table {
    text-align: center;
    padding: 60px !important;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: #94a3b8;
  }

  .empty-state i {
    font-size: 3rem;
    opacity: 0.5;
  }

  .empty-state p {
    margin: 0;
    font-size: 1.1rem;
  }


        .officer-name {
          font-weight: 600;
          color: white;
        }

        .officer-rank {
          font-size: 0.85rem;
          color: #94a3b8;
        }

        .reason-cell {
          max-width: 200px;
          color: #cbd5e1;
          font-size: 0.9rem;
        }
       .details-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
        }

        .details-btn:hover {
          background: #2563eb;
          transform: translateY(-2px);
        }

        @media (max-width: 1024px) {
          .filter-group {
            flex-direction: column;
          }

          .filter-input,
          .filter-select,
          .date-filters {
            min-width: 100%;
          }

          .clear-filters {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .filters-section {
            padding: 16px;
          }

          .date-filters {
            flex-direction: column;
            gap: 12px;
          }

          .date-separator {
            display: none;
          }

          .export-buttons {
            flex-direction: column;
          }

          .export-btn {
            width: 100%;
            justify-content: center;
          }
        }
          /* === TABLE STYLES - ADD THESE === */

.table-container {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.table-responsive {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.data-table thead {
  background: rgba(30, 41, 59, 0.9);
}

.data-table th {
  padding: 16px 12px;
  text-align: left;
  color: #cbd5e1;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid rgba(59, 130, 246, 0.3);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.data-table th:hover {
  background: rgba(51, 65, 85, 0.8);
}

.data-table th span {
  margin-right: 8px;
}

.data-table th i {
  font-size: 0.8rem;
  opacity: 0.7;
}

.data-table td {
  padding: 16px 12px;
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  vertical-align: middle;
}

.data-table tbody tr {
  background: rgba(15, 23, 42, 0.3);
  transition: background-color 0.2s ease;
}

.data-table tbody tr:nth-child(even) {
  background: rgba(15, 23, 42, 0.2);
}

.data-table tbody tr:hover {
  background: rgba(30, 41, 59, 0.5);
}

/* Empty state styles */
.empty-table {
  text-align: center;
  padding: 60px !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 1.1rem;
}

/* Guard info styles */
.guard-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.guard-name {
  font-weight: 600;
  color: white;
}

.guard-email {
  font-size: 0.85rem;
  color: #94a3b8;
}

/* Rank badge styles */
.rank-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.85rem;
  display: inline-block;
  white-space: nowrap;
}

.rank-badge.rank-a {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.rank-badge.rank-b {
  background: rgba(139, 92, 246, 0.2);
  color: #8b5cf6;
}

.rank-badge.rank-c {
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

/* Status badge styles - CRITICAL! */
.status-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.85rem;
  display: inline-block;
  white-space: nowrap;
}

.status-badge.active {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.status-badge.inactive {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.status-badge.assigned {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}

.status-badge.unassigned {
  background: rgba(148, 163, 184, 0.2);
  color: #94a3b8;
}

.status-badge.pending {
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

/* Other cell styles */
.experience-cell {
  font-weight: 600;
  color: white;
}

.vip-cell {
  color: white;
  font-weight: 500;
}

.date-cell {
  color: #94a3b8;
  font-size: 0.9rem;
  white-space: nowrap;
}

.actions-cell {
  white-space: nowrap;
}

/* Responsive table styles */
@media (max-width: 768px) {
  .table-container {
    padding: 16px;
  }
  
  .table-title {
    font-size: 1.25rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 12px 8px;
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .data-table th span {
    display: block;
    margin-right: 0;
  }
  
  .data-table th i {
    display: none;
  }
}
      `}</style>
    </>
  );
}

// Reports View Component
function ReportsView({ data }) {
  const guardData = useMemo(() => {
    const guards = [];
    (data.assignments || []).forEach(assignment => {
      const officers = assignment.officers || [];
      const vip = assignment.category;

      officers.forEach(officer => {
        guards.push({
          assignmentId: assignment.id,
          officer: officer,
          assignmentStatus: assignment.status,
          assignedAt: assignment.assignedAt,
          startAt: assignment.startAt,
          endAt: assignment.endAt,
          vip: vip
        });
      });
    });
    return guards;
  }, [data.assignments]);

  const exportComprehensivePDF = () => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(18);
      doc.text('Comprehensive Security Assignment Report', 14, 22);
      doc.setFontSize(11);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      let yPos = 40;

      doc.setFontSize(14);
      doc.text('1. Assignment Summary', 14, yPos);
      yPos += 10;

      const assignments = data.assignments || [];
      const totalAssignments = assignments.length;
      const activeDuty = assignments.filter(a => a.status === 'Active Duty' || a.status === 'Active').length;
      const canceled = assignments.filter(a => a.status === 'Canceled' || a.status === 'Rejected').length;

      autoTable(doc, {
        head: [['Metric', 'Count']],
        body: [
          ['Total Assignments', totalAssignments],
          ['Active Duty', activeDuty],
          ['Canceled', canceled]
        ],
        startY: yPos,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [41, 128, 185] }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      doc.setFontSize(14);
      doc.text('2. VIP Assignments', 14, yPos);
      yPos += 10;

      autoTable(doc, {
        head: [['ID', 'VIP Name', 'Designation', 'Status', 'Assigned At']],
        body: assignments.slice(0, 20).map(a => [
          a.id || 'N/A',
          a.category ? (a.category.name || a.category.username || 'No VIP') : 'No VIP',
          a.category?.designation || 'N/A',
          a.status || 'N/A',
          formatDate(a.assignedAt)
        ]),
        startY: yPos,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [52, 152, 219] }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      doc.setFontSize(14);
      doc.text('3. Guard Assignment Summary', 14, yPos);
      yPos += 10;

      autoTable(doc, {
        head: [['Guard Name', 'Rank', 'Status', 'Experience', 'Assignment ID']],
        body: guardData.slice(0, 15).map(g => [
          g.officer.name || g.officer.username || 'N/A',
          g.officer.rank || 'N/A',
          g.officer.status || 'N/A',
          g.officer.experience || 0,
          g.assignmentId || 'N/A'
        ]),
        startY: yPos,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [46, 204, 113] }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      doc.setFontSize(14);
      doc.text('4. Replacement History', 14, yPos);
      yPos += 10;

      const replacements = data.replacedOfficers || [];
      autoTable(doc, {
        head: [['ID', 'Previous Officer', 'Current Officer', 'Reason', 'Status']],
        body: replacements.slice(0, 10).map(r => [
          r.id || 'N/A',
          r.previousOfficer ? (r.previousOfficer.name || r.previousOfficer.username || 'N/A') : 'N/A',
          r.currentOfficer ? (r.currentOfficer.name || r.currentOfficer.username || 'Not Assigned') : 'Not Assigned',
          r.reason || r.reasonMessage || 'N/A',
          r.status || 'N/A'
        ]),
        startY: yPos,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [231, 76, 60] }
      });

      doc.save(`comprehensive_report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Comprehensive PDF Export Error:', error);
      alert('Failed to export comprehensive PDF. Please try again.');
    }
  };

  const exportComprehensiveExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      const assignmentsData = data.assignments.map(a => ({
        'Assignment ID': a.id || 'N/A',
        'VIP Name': a.category ? (a.category.name || a.category.username || 'No VIP') : 'No VIP',
        'VIP Designation': a.category?.designation || 'N/A',
        'VIP Email': a.category?.email || 'N/A',
        'Status': a.status || 'N/A',
        'Times Assigned': a.timesAssigned || 0,
        'Assigned At': formatDateTime(a.assignedAt),
        'Start At': formatDateTime(a.startAt),
        'End At': formatDateTime(a.endAt)
      }));
      const ws1 = XLSX.utils.json_to_sheet(assignmentsData);
      XLSX.utils.book_append_sheet(wb, ws1, 'Assignments');

      const guardDataForExport = guardData.map(g => ({
        'Assignment ID': g.assignmentId || 'N/A',
        'Guard Name': g.officer.name || g.officer.username || 'N/A',
        'Guard Rank': g.officer.rank || 'N/A',
        'Guard Status': g.officer.status || 'N/A',
        'Experience': g.officer.experience || 0,
        'Email': g.officer.email || 'N/A',
        'Contact': g.officer.contactno || 'N/A',
        'Aadhar': g.officer.adharNo || 'N/A',
        'VIP Name': g.vip ? (g.vip.name || g.vip.username || 'No VIP') : 'No VIP',
        'Assignment Status': g.assignmentStatus || 'N/A',
        'Assigned At': formatDateTime(g.assignedAt)
      }));
      const ws2 = XLSX.utils.json_to_sheet(guardDataForExport);
      XLSX.utils.book_append_sheet(wb, ws2, 'Guards');

      const replacementsData = data.replacedOfficers.map(r => ({
        'Replacement ID': r.id || 'N/A',
        'Assignment ID': r.assignmentId || 'N/A',
        'Previous Officer': r.previousOfficer ? (r.previousOfficer.name || r.previousOfficer.username || 'N/A') : 'N/A',
        'Previous Rank': r.previousOfficer?.rank || 'N/A',
        'Current Officer': r.currentOfficer ? (r.currentOfficer.name || r.currentOfficer.username || 'Not Assigned') : 'Not Assigned',
        'Current Rank': r.currentOfficer?.rank || 'N/A',
        'Reason': r.reason || 'N/A',
        'Reason Message': r.reasonMessage || '',
        'Status': r.status || 'N/A',
        'Accepted By': r.acceptedBy || 'N/A',
        'Replaced At': formatDateTime(r.replacedAt || r.createdAt)
      }));
      const ws3 = XLSX.utils.json_to_sheet(replacementsData);
      XLSX.utils.book_append_sheet(wb, ws3, 'Replacements');

      XLSX.writeFile(wb, `comprehensive_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Comprehensive Excel Export Error:', error);
      alert('Failed to export comprehensive Excel. Please try again.');
    }
  };

  return (
    <>
      <div className="reports-grid">
        <div className="report-card">
          <div className="report-icon" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>
            <i className="fas fa-file-pdf"></i>
          </div>
          <h4>Comprehensive Reports</h4>
          <p className="report-description">Export all data in a single comprehensive report with multiple sheets/sections.</p>
          <div className="report-actions">
            <button className="report-btn pdf" onClick={exportComprehensivePDF}>
              <i className="fas fa-file-pdf"></i> Comprehensive PDF
            </button>
            <button className="report-btn excel" onClick={exportComprehensiveExcel}>
              <i className="fas fa-file-excel"></i> Comprehensive Excel
            </button>
          </div>
        </div>

        <div className="report-card">
          <div className="report-icon" style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' }}>
            <i className="fas fa-clipboard-list"></i>
          </div>
          <h4>Individual Reports</h4>
          <p className="report-description">Export specific data types individually for focused analysis.</p>
          <div className="report-actions">
            <div className="report-buttons-grid">
              <button className="report-btn small pdf"
                onClick={() => exportToPDF(
                  data.assignments,
                  'All Assignments',
                  ['ID', 'VIP Name', 'Designation', 'Status', 'Assigned At'],
                  (a) => [
                    a.id || 'N/A',
                    a.category ? (a.category.name || a.category.username || 'No VIP') : 'No VIP',
                    a.category?.designation || 'N/A',
                    a.status || 'N/A',
                    formatDate(a.assignedAt)
                  ]
                )}>
                <i className="fas fa-file-pdf"></i> All Assignments
              </button>
              <button className="report-btn small excel"
                onClick={() => exportToExcel(
                  data.assignments,
                  'All Assignments',
                  (a) => ({
                    'Assignment ID': a.id || 'N/A',
                    'VIP Name': a.category ? (a.category.name || a.category.username || 'No VIP') : 'No VIP',
                    'Designation': a.category?.designation || 'N/A',
                    'Status': a.status || 'N/A',
                    'Assigned At': formatDateTime(a.assignedAt)
                  })
                )}>
                <i className="fas fa-file-excel"></i> All Assignments
              </button>
              <button className="report-btn small pdf"
                onClick={() => exportToPDF(
                  guardData,
                  'All Guards',
                  ['Assignment ID', 'Guard Name', 'Rank', 'Status', 'VIP Name'],
                  (g) => [
                    g.assignmentId || 'N/A',
                    g.officer.name || g.officer.username || 'N/A',
                    g.officer.rank || 'N/A',
                    g.officer.status || 'N/A',
                    g.vip ? (g.vip.name || g.vip.username || 'No VIP') : 'No VIP'
                  ]
                )}>
                <i className="fas fa-file-pdf"></i> All Guards
              </button>
              <button className="report-btn small excel"
                onClick={() => exportToExcel(
                  guardData,
                  'All Guards',
                  (g) => ({
                    'Assignment ID': g.assignmentId || 'N/A',
                    'Guard Name': g.officer.name || g.officer.username || 'N/A',
                    'Rank': g.officer.rank || 'N/A',
                    'Status': g.officer.status || 'N/A',
                    'VIP Name': g.vip ? (g.vip.name || g.vip.username || 'No VIP') : 'No VIP'
                  })
                )}>
                <i className="fas fa-file-excel"></i> All Guards
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-card">
        <div className="stats-header">
          <i className="fas fa-chart-bar"></i>
          <h4>Quick Statistics</h4>
        </div>
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-number">{data.assignments?.length || 0}</div>
            <div className="stat-label">Total Assignments</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{data.assignments?.filter(a => a.status === 'Active Duty' || a.status === 'Active').length || 0}</div>
            <div className="stat-label">Active Duty</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{guardData.length}</div>
            <div className="stat-label">Officer Assignments</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{data.replacedOfficers?.length || 0}</div>
            <div className="stat-label">Replacements</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .reports-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
          margin-bottom: 30px;
        }

        .report-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 24px;
          transition: all 0.3s ease;
        }

        .report-card:hover {
          transform: translateY(-5px);
          border-color: #3b82f6;
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.15);
        }

        .report-icon {
          width: 70px;
          height: 70px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          font-size: 2rem;
        }

        .report-card h4 {
          font-size: 1.3rem;
          color: white;
          margin: 0 0 12px 0;
        }

        .report-description {
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 20px;
        }

        .report-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .report-btn {
          padding: 12px 20px;
          border-radius: 12px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 0.9rem;
        }

        .report-btn.pdf {
          background: #ef4444;
          color: white;
        }

        .report-btn.pdf:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .report-btn.excel {
          background: #10b981;
          color: white;
        }

        .report-btn.excel:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .report-buttons-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .report-btn.small {
          padding: 10px 12px;
          font-size: 0.85rem;
        }

        .stats-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 24px;
          margin-top: 20px;
        }

        .stats-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
        }

        .stats-header i {
          font-size: 1.5rem;
          color: #60a5fa;
        }

        .stats-header h4 {
          font-size: 1.3rem;
          color: white;
          margin: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 20px;
        }

        .stat-item {
          text-align: center;
          padding: 20px;
          background: rgba(30, 41, 59, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .stat-item .stat-number {
          font-size: 1.8rem;
          font-weight: 800;
          color: #60a5fa;
          margin-bottom: 8px;
        }

        .stat-item .stat-label {
          color: #94a3b8;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .reports-grid {
            grid-template-columns: 1fr;
          }

          .report-buttons-grid {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
}

// Assignment Details Modal
function AssignmentDetailsModal({ assignment, onClose }) {
  const vip = assignment.category;
  const officers = assignment.officers || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            <i className="fas fa-info-circle"></i>
            Assignment Details - ID: {assignment.id || 'N/A'}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="modal-body">
          <div className="info-grid">
            <div className="info-section">
              <h4 className="info-title">
                <i className="fas fa-clipboard-check"></i>
                Assignment Information
              </h4>
              <div className="info-grid-small">
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className={`status-badge ${getStatusClass(assignment.status)}`}>
                    {assignment.status || 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Times Assigned:</span>
                  <span className="info-value">{assignment.timesAssigned || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Assigned At:</span>
                  <span className="info-value">{formatDateTime(assignment.assignedAt)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Start At:</span>
                  <span className="info-value">{formatDateTime(assignment.startAt)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">End At:</span>
                  <span className="info-value">{formatDateTime(assignment.endAt)}</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4 className="info-title">
                <i className="fas fa-user-tie"></i>
                VIP Information
              </h4>
              {vip ? (
                <div className="info-grid-small">
                  <div className="info-item">
                    <span className="info-label">Name:</span>
                    <span className="info-value">{vip.name || vip.username || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Designation:</span>
                    <span className="info-value">{vip.designation || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{vip.email || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Contact:</span>
                    <span className="info-value">{vip.contactno || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span className={`status-badge ${getStatusClass(vip.status)}`}>
                      {vip.status || 'N/A'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="no-info">
                  <i className="fas fa-user-slash"></i>
                  <p>No VIP information</p>
                </div>
              )}
            </div>
          </div>

          <div className="info-section">
            <h4 className="info-title">
              <i className="fas fa-user-shield"></i>
              Assigned Officers ({officers.length})
            </h4>
            {officers.length > 0 ? (
              <div className="officers-grid">
                {officers.map((officer, index) => (
                  <div key={index} className="officer-card">
                    <div className="officer-header">
                      <h5 className="officer-name">{officer.name || officer.username || 'N/A'}</h5>
                      <span className={`rank-badge ${getRankClass(officer.rank)}`}>
                        {officer.rank || 'N/A'}
                      </span>
                    </div>
                    <div className="officer-details">
                      <div className="officer-detail">
                        <span className="detail-label">Status:</span>
                        <span className={`status-badge ${getStatusClass(officer.status)}`}>
                          {officer.status || 'N/A'}
                        </span>
                      </div>
                      <div className="officer-detail">
                        <span className="detail-label">Experience:</span>
                        <span className="detail-value">{officer.experience || 0} years</span>
                      </div>
                      <div className="officer-detail">
                        <span className="detail-label">Contact:</span>
                        <span className="detail-value">{officer.contactno || 'N/A'}</span>
                      </div>
                      {officer.email && (
                        <div className="officer-detail">
                          <span className="detail-label">Email:</span>
                          <span className="detail-value">{officer.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-info">
                <i className="fas fa-user-slash"></i>
                <p>No officers assigned</p>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn close" onClick={onClose}>
            <i className="fas fa-times"></i> Close
          </button>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          border-radius: 20px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid rgba(59, 130, 246, 0.4);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .modal-header {
          padding: 24px 30px;
          border-bottom: 1px solid rgba(59, 130, 246, 0.3);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 1.4rem;
          font-weight: 600;
          color: white;
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 0;
        }

        .modal-title i {
          color: #60a5fa;
        }

        .modal-close {
          background: transparent;
          border: none;
          color: #94a3b8;
          font-size: 1.2rem;
          cursor: pointer;
          transition: color 0.3s ease;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close:hover {
          color: white;
          background: rgba(239, 68, 68, 0.1);
        }

        .modal-body {
          padding: 30px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
          margin-bottom: 30px;
        }

        .info-section {
          background: rgba(15, 23, 42, 0.6);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .info-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: white;
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 20px 0;
        }

        .info-title i {
          color: #60a5fa;
          font-size: 1rem;
        }

        .info-grid-small {
          display: grid;
          gap: 16px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .info-label {
          color: #94a3b8;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .info-value {
          color: white;
          font-size: 0.95rem;
          font-weight: 500;
        }

        .no-info {
          text-align: center;
          padding: 30px 20px;
          color: #64748b;
        }

        .no-info i {
          font-size: 2.5rem;
          margin-bottom: 15px;
          opacity: 0.5;
        }

        .no-info p {
          margin: 0;
          font-size: 1rem;
        }

        .officers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 16px;
        }

        .officer-card {
          background: rgba(30, 41, 59, 0.8);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(59, 130, 246, 0.2);
          transition: all 0.3s ease;
        }

        .officer-card:hover {
          transform: translateY(-2px);
          border-color: #3b82f6;
          box-shadow: 0 5px 15px rgba(59, 130, 246, 0.15);
        }

        .officer-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .officer-name {
          font-size: 1rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .officer-details {
          display: grid;
          gap: 10px;
        }

        .officer-detail {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .detail-label {
          color: #94a3b8;
          font-size: 0.85rem;
        }

        .detail-value {
          color: white;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .modal-footer {
          padding: 20px 30px;
          border-top: 1px solid rgba(59, 130, 246, 0.3);
          display: flex;
          justify-content: flex-end;
        }

        .modal-btn {
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          border: none;
          font-size: 0.95rem;
        }

        .modal-btn.close {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 2px solid rgba(239, 68, 68, 0.4);
        }

        .modal-btn.close:hover {
          background: rgba(239, 68, 68, 0.3);
          transform: translateY(-2px);
        }

        @media (max-width: 768px) {
          .modal-content {
            max-height: 95vh;
          }

          .modal-header,
          .modal-body,
          .modal-footer {
            padding: 20px;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .officers-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .modal-title {
            font-size: 1.2rem;
          }

          .info-section {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
}

// Replacement Details Modal - Fixed version
function ReplacementDetailsModal({ replacement, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            <i className="fas fa-exchange-alt"></i>
            Replacement Details - ID: {replacement.id || 'N/A'}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="modal-body">
          <div className="info-section">
            <h4 className="info-title">
              <i className="fas fa-info-circle"></i>
              Replacement Information
            </h4>
            <div className="info-grid-small">
              <div className="info-item">
                <span className="info-label">Assignment ID:</span>
                <span className="info-value">{replacement.assignmentId || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Reason:</span>
                <span className="info-value">{replacement.reason || replacement.reasonMessage || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Accepted By:</span>
                <span className="info-value">{replacement.acceptedBy || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className={`status-badge ${getStatusClass(replacement.status)}`}>
                  {replacement.status || 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Replaced At:</span>
                <span className="info-value">{formatDateTime(replacement.replacedAt || replacement.createdAt)}</span>
              </div>
            </div>
          </div>

          <div className="officers-comparison">
            <div className="officer-column">
              <h4 className="info-title">
                <i className="fas fa-user-minus"></i>
                Previous Officer
              </h4>
              {replacement.previousOfficer ? (
                <div className="officer-card">
                  <div className="officer-header">
                    <h5 className="officer-name">
                      {replacement.previousOfficer.name || replacement.previousOfficer.username || 'N/A'}
                    </h5>
                    {replacement.previousOfficer.rank && (
                      <span className={`rank-badge ${getRankClass(replacement.previousOfficer.rank)}`}>
                        {replacement.previousOfficer.rank}
                      </span>
                    )}
                  </div>
                  <div className="officer-details">
                    <div className="officer-detail">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${getStatusClass(replacement.previousOfficer.status)}`}>
                        {replacement.previousOfficer.status || 'N/A'}
                      </span>
                    </div>
                    {replacement.previousOfficer.email && (
                      <div className="officer-detail">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">{replacement.previousOfficer.email}</span>
                      </div>
                    )}
                    {replacement.previousOfficer.contactno && (
                      <div className="officer-detail">
                        <span className="detail-label">Contact:</span>
                        <span className="detail-value">{replacement.previousOfficer.contactno}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-info">
                  <i className="fas fa-user-slash"></i>
                  <p>No previous officer</p>
                </div>
              )}
            </div>

            <div className="officer-column">
              <h4 className="info-title">
                <i className="fas fa-user-plus"></i>
                Current Officer
              </h4>
              {replacement.currentOfficer ? (
                <div className="officer-card">
                  <div className="officer-header">
                    <h5 className="officer-name">
                      {replacement.currentOfficer.name || replacement.currentOfficer.username || 'N/A'}
                    </h5>
                    {replacement.currentOfficer.rank && (
                      <span className={`rank-badge ${getRankClass(replacement.currentOfficer.rank)}`}>
                        {replacement.currentOfficer.rank}
                      </span>
                    )}
                  </div>
                  <div className="officer-details">
                    <div className="officer-detail">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${getStatusClass(replacement.currentOfficer.status)}`}>
                        {replacement.currentOfficer.status || 'N/A'}
                      </span>
                    </div>
                    {replacement.currentOfficer.email && (
                      <div className="officer-detail">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">{replacement.currentOfficer.email}</span>
                      </div>
                    )}
                    {replacement.currentOfficer.contactno && (
                      <div className="officer-detail">
                        <span className="detail-label">Contact:</span>
                        <span className="detail-value">{replacement.currentOfficer.contactno}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-info">
                  <i className="fas fa-user-slash"></i>
                  <p>No current officer</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn close" onClick={onClose}>
            <i className="fas fa-times"></i> Close
          </button>
        </div>
      </div>

      <style jsx>{`
        .officers-comparison {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 30px;
          margin-top: 24px;
        }

        .officer-column {
          display: flex;
          flex-direction: column;
        }

        @media (max-width: 768px) {
          .officers-comparison {
            grid-template-columns: 1fr;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
}

// CSS Styles (you'll need to add this to your global CSS or component CSS)
// Copy all the CSS from the original HTML file into your project's CSS

// import React, { useEffect, useMemo, useState } from "react";
// import axios from "axios";
// import { useGuardStore } from "../context/GuardContext";
// import { useVipStore } from "../context/VipContext";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";

// const BASE_URL = process.env.REACT_APP_BASE_URL;
// const PER_PAGE = 10;

// export default function AdminUserVipHistory() {
//   const { setSelectedGuard } = useGuardStore();
//   const { setSelectedVip } = useVipStore();
//   const [expandedVipId, setExpandedVipId] = useState(null);

//   const [data, setData] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedRow, setSelectedRow] = useState(null);
//   const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

//   // New state variables for enhanced dashboard
//   const [currentView, setCurrentView] = useState("vip-view"); // 'vip-view' or 'guard-view'
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // Filter states
//   const [filters, setFilters] = useState({
//     status: "all",
//     rank: "all",
//     vipStatus: "all",
//     guardStatus: "all",
//     experience: "all",
//     timesAssigned: "all",
//     dateFrom: "",
//     dateTo: "",
//     search: "",
//   });

//   // Sorting states
//   const [sortConfig, setSortConfig] = useState({
//     vip: { field: "name", direction: "asc" },
//     guard: { field: "name", direction: "asc" },
//   });

//   // Calculate statistics
//   const stats = useMemo(() => {
//     const totalAssignments = data.length;

//     const activeVips = new Set();
//     const activeGuards = new Set();

//     data.forEach((a) => {
//       if (a.vip?.status === "Active") activeVips.add(a.vip.id);

//       a.officers.forEach((o) => {
//         if (o.status === "Active") activeGuards.add(o.id);
//       });
//     });

//     return {
//       totalAssignments,
//       activeVips: activeVips.size,
//       activeGuards: activeGuards.size,
//     };
//   }, [data]);

//   const normalizeAssignments = (assignments = [], replaced = []) => {
//     const map = new Map();

//     // Step 1: Base assignments
//     assignments.forEach((a) => {
//       map.set(a.id, {
//         assignmentId: a.id,
//         vip: a.category?.[0] || null,
//         status: a.status,
//         assignedAt: a.assignedAt,
//         startAt: a.startAt,
//         atEnd: a.atEnd,
//         officers: (a.officer || []).map((o) => ({
//           ...o,
//           isReplaced: false,
//         })),
//         replacements: [],
//       });
//     });

//     // Step 2: Merge replacements INTO SAME assignmentId
//     replaced.forEach((r) => {
//       const assignmentId = r.userGuardAssignment?.id;
//       if (!map.has(assignmentId)) return;

//       map.get(assignmentId).replacements.push({
//         previousOfficer: r.previousOfficer,
//         currentOfficer: r.currentOfficer,
//         reason: r.reason,
//         reasonMessage: r.reasonMessage,
//         acceptedBy: r.acceptedBy,
//         status: r.status,
//       });
//     });

//     return Array.from(map.values());
//   };

//   useEffect(() => {
//     fetchAllData();
//     // Set default date range
//     const today = new Date().toISOString().split("T")[0];
//     const thirtyDaysAgo = new Date();
//     thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

//     setFilters((prev) => ({
//       ...prev,
//       dateFrom: thirtyDaysAgo.toISOString().split("T")[0],
//       dateTo: today,
//     }));
//   }, []);

//   const fetchAllData = async () => {
//     try {
//       setLoading(true);
//       const role = localStorage.getItem("role");
//       const token = localStorage.getItem(`${role}Token`);

//       const res = await axios.get(`${BASE_URL}/api/assignments/getall`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       const { assignments = [], replacedOfficers = [] } = res.data || {};
//       const normalized = normalizeAssignments(assignments, replacedOfficers);
//       setData(normalized);
//     } catch (err) {
//       console.log("Admin User History Error:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatDateOnly = (date) => {
//     if (!date) return "N/A";
//     return new Date(date).toLocaleDateString("en-IN", {
//       day: "2-digit",
//       month: "short",
//       year: "numeric",
//     });
//   };

//   // Helper: Apply UI filters to raw data and return filtered items
//   const getFilteredItems = () => {
//     if (!data || !Array.isArray(data)) return [];
//     return data.filter((item) => {
//       // Status filter
//       if (filters.status !== "all" && item.status !== filters.status) {
//         return false;
//       }

//       // Rank filter
//       if (
//         filters.rank !== "all" &&
//         !item.officers?.some((o) => o.rank === filters.rank)
//       ) {
//         return false;
//       }

//       // VIP status filter
//       if (
//         filters.vipStatus !== "all" &&
//         item.vip?.status !== filters.vipStatus
//       ) {
//         return false;
//       }

//       // Guard status filter
//       if (
//         filters.guardStatus !== "all" &&
//         !item.officers?.some((o) => o.status === filters.guardStatus)
//       ) {
//         return false;
//       }

//       // Experience filter
//       if (filters.experience !== "all") {
//         const hasMatchingExp = item.officers?.some((o) => {
//           const exp = o.experience || 0;
//           switch (filters.experience) {
//             case "0-5":
//               return exp >= 0 && exp <= 5;
//             case "6-10":
//               return exp >= 6 && exp <= 10;
//             case "11-15":
//               return exp >= 11 && exp <= 15;
//             case "16-20":
//               return exp >= 16 && exp <= 20;
//             case "20+":
//               return exp >= 20;
//             default:
//               return true;
//           }
//         });
//         if (!hasMatchingExp) return false;
//       }

//       // Date range filter
//       if (filters.dateFrom || filters.dateTo) {
//         const assignedDate = item.assignedAt
//           ? new Date(item.assignedAt).toISOString().split("T")[0]
//           : "";
        
//         if (filters.dateFrom && assignedDate < filters.dateFrom) {
//           return false;
//         }
//         if (filters.dateTo && assignedDate > filters.dateTo) {
//           return false;
//         }
//       }

//       // Search filter
//       if (filters.search) {
//         const searchTerm = filters.search.toLowerCase();
//         const vipName = item.vip?.name?.toLowerCase() || "";
//         const vipEmail = item.vip?.email?.toLowerCase() || "";
//         const vipDesignation = item.vip?.designation?.toLowerCase() || "";

//         const guardNames = item.officers
//           ?.map((o) => o.name?.toLowerCase() || "")
//           .join(" ");

//         const guardEmails = item.officers
//           ?.map((o) => o.email?.toLowerCase() || "")
//           .join(" ");

//         if (
//           !vipName.includes(searchTerm) &&
//           !vipEmail.includes(searchTerm) &&
//           !vipDesignation.includes(searchTerm) &&
//           !guardNames.includes(searchTerm) &&
//           !guardEmails.includes(searchTerm)
//         ) {
//           return false;
//         }
//       }

//       return true;
//     });
//   };

//   // Filter and process data for UI (with grouping + pagination)
//   const processedData = useMemo(() => {
//     let filtered = getFilteredItems();

//     // Process based on view
//     if (currentView === "vip-view") {
//       // For VIP view, we need to group by VIP and then paginate
//       const vipMap = new Map();

//       filtered.forEach((assignment) => {
//         const vip = assignment.vip;
//         if (!vip) return;

//         if (!vipMap.has(vip.id)) {
//           vipMap.set(vip.id, {
//             ...vip,
//             assignments: [],
//             totalGuards: 0,
//             guardRanks: new Set(),
//             assignmentStatuses: new Set(),
//             assignmentDates: [],
//             timesAssigned: 0,
//           });
//         }

//         const vipData = vipMap.get(vip.id);
//         vipData.assignments.push(assignment);
//         vipData.totalGuards += assignment.officers.length;
//         assignment.officers.forEach((o) => {
//           if (o.rank) vipData.guardRanks.add(o.rank);
//         });
//         vipData.assignmentStatuses.add(assignment.status);
//         vipData.assignmentDates.push(new Date(assignment.assignedAt));
//         vipData.timesAssigned += 1;
//       });

//       // Convert Map to array and calculate derived properties
//       const vipArray = Array.from(vipMap.values()).map((vip) => ({
//         ...vip,
//         guardRanks: Array.from(vip.guardRanks).join(", "),
//         assignmentStatuses: Array.from(vip.assignmentStatuses).join(", "),
//         latestDate: vip.assignmentDates.length > 0
//           ? new Date(Math.max(...vip.assignmentDates.map(d => d.getTime())))
//           : null,
//       }));

//       // Apply sorting for VIP view
//       vipArray.sort((a, b) => {
//         let aValue, bValue;
//         const sortField = sortConfig.vip.field;
//         const sortDirection = sortConfig.vip.direction;

//         switch (sortField) {
//           case "name":
//             aValue = a.name || "";
//             bValue = b.name || "";
//             break;
//           case "designation":
//             aValue = a.designation || "";
//             bValue = b.designation || "";
//             break;
//           case "status":
//             aValue = a.status || "";
//             bValue = b.status || "";
//             break;
//           case "guard-rank":
//             aValue = a.guardRanks || "";
//             bValue = b.guardRanks || "";
//             break;
//           case "date":
//             aValue = a.latestDate?.getTime() || 0;
//             bValue = b.latestDate?.getTime() || 0;
//             break;
//           default:
//             aValue = a.name || "";
//             bValue = b.name || "";
//         }

//         if (typeof aValue === "string" && typeof bValue === "string") {
//           return sortDirection === "asc"
//             ? aValue.localeCompare(bValue)
//             : bValue.localeCompare(aValue);
//         } else {
//           return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
//         }
//       });

//       // Pagination
//       const total = vipArray.length;
//       const pages = Math.ceil(total / PER_PAGE);
//       setTotalPages(pages);

//       const startIndex = (currentPage - 1) * PER_PAGE;
//       const endIndex = Math.min(startIndex + PER_PAGE, total);

//       return {
//         type: "vip",
//         data: vipArray.slice(startIndex, endIndex),
//         total,
//       };
//     } else {
//       // Guard view - group by guard (FIXED: renamed variable to avoid conflict)
//       const guardMapping = new Map();

//       filtered.forEach((assignment) => {
//         // Current officers
//         assignment.officers.forEach((o) => {
//           if (!guardMapping.has(o.id)) {
//             guardMapping.set(o.id, {
//               ...o,
//               assignments: [],
//               vipDesignations: new Set(),
//               vipNames: new Set(),
//             });
//           }

//           const guard = guardMapping.get(o.id);
//           guard.assignments.push({
//             assignmentId: assignment.assignmentId,
//             assignmentStatus: assignment.status,
//             assignedAt: new Date(assignment.assignedAt),
//             vip: assignment.vip,
//           });

//           if (assignment.vip?.designation) {
//             guard.vipDesignations.add(assignment.vip.designation);
//           }
//           if (assignment.vip?.name) {
//             guard.vipNames.add(assignment.vip.name);
//           }
//         });

//         // Replaced officers
//         assignment.replacements.forEach((r) => {
//           if (!r.previousOfficer) return;

//           if (!guardMapping.has(r.previousOfficer.id)) {
//             guardMapping.set(r.previousOfficer.id, {
//               ...r.previousOfficer,
//               status: "Replaced",
//               assignments: [],
//               vipDesignations: new Set(),
//               vipNames: new Set(),
//             });
//           }

//           const guard = guardMapping.get(r.previousOfficer.id);
//           guard.assignments.push({
//             assignmentId: assignment.assignmentId,
//             assignmentStatus: "Replaced",
//             assignedAt: new Date(assignment.assignedAt),
//             vip: assignment.vip,
//           });

//           if (assignment.vip?.designation) {
//             guard.vipDesignations.add(assignment.vip.designation);
//           }
//           if (assignment.vip?.name) {
//             guard.vipNames.add(assignment.vip.name);
//           }
//         });
//       });

//       // Convert Map to array
//       const guardArray = Array.from(guardMapping.values()).map((guard) => ({
//         ...guard,
//         vipDesignations: Array.from(guard.vipDesignations).join(", "),
//         vipNames: Array.from(guard.vipNames).join(", "),
//         latestAssignment: guard.assignments.length > 0
//           ? new Date(Math.max(...guard.assignments.map(a => a.assignedAt.getTime())))
//           : null,
//       }));

//       // Apply sorting for guard view
//       guardArray.sort((a, b) => {
//         let aValue, bValue;
//         const sortField = sortConfig.guard.field;
//         const sortDirection = sortConfig.guard.direction;

//         switch (sortField) {
//           case "name":
//             aValue = a.name || "";
//             bValue = b.name || "";
//             break;
//           case "rank":
//             aValue = a.rank || "";
//             bValue = b.rank || "";
//             break;
//           case "status":
//             aValue = a.status || "";
//             bValue = b.status || "";
//             break;
//           case "experience":
//             aValue = a.experience || 0;
//             bValue = b.experience || 0;
//             break;
//           case "vip-designation":
//             aValue = a.vipDesignations || "";
//             bValue = b.vipDesignations || "";
//             break;
//           case "assignment-status":
//             aValue = a.assignments.length > 0 ? a.assignments[0].assignmentStatus : "";
//             bValue = b.assignments.length > 0 ? b.assignments[0].assignmentStatus : "";
//             break;
//           case "date":
//             aValue = a.latestAssignment?.getTime() || 0;
//             bValue = b.latestAssignment?.getTime() || 0;
//             break;
//           case "times-assigned":
//             aValue = a.assignments.length || 0;
//             bValue = b.assignments.length || 0;
//             break;
//           default:
//             aValue = a.name || "";
//             bValue = b.name || "";
//         }

//         if (typeof aValue === "string" && typeof bValue === "string") {
//           return sortDirection === "asc"
//             ? aValue.localeCompare(bValue)
//             : bValue.localeCompare(aValue);
//         } else {
//           return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
//         }
//       });

//       // Pagination
//       const total = guardArray.length;
//       const pages = Math.ceil(total / PER_PAGE);
//       setTotalPages(pages);

//       const startIndex = (currentPage - 1) * PER_PAGE;
//       const endIndex = Math.min(startIndex + PER_PAGE, total);

//       return {
//         type: "guard",
//         data: guardArray.slice(startIndex, endIndex),
//         total,
//       };
//     }
//   }, [data, currentView, filters, sortConfig, currentPage]);

//   // Get status badge class
//   const getStatusClass = (status) => {
//     switch (status?.toLowerCase()) {
//       case "completed":
//       case "complete":
//         return "status-completed";
//       case "active duty":
//       case "active":
//         return "status-active";
//       case "replaced":
//         return "status-replaced";
//       case "pending":
//         return "status-pending";
//       case "canceled":
//       case "cancelled":
//         return "status-canceled";
//       case "deviation":
//       case "incident":
//         return "status-incident";
//       default:
//         return "status-pending";
//     }
//   };

//   const getRankClass = (rank) => {
//     if (!rank) return "rank-a";
//     const grade = rank.charAt(0).toLowerCase();
//     return `rank-${grade}`;
//   };

//   const handleRowClick = (item, e) => {
//     if (currentView === "vip-view") {
//       setSelectedVip(item || null);
//       // Find guards assigned to this VIP
//       if (item.assignments && item.assignments.length > 0) {
//         const guards = item.assignments.flatMap(a => a.officers);
//         if (guards.length > 0) {
//           setSelectedGuard(guards[0]);
//         }
//       }
//     } else {
//       setSelectedGuard(item || null);
//       // Find VIPs assigned to this guard
//       if (item.assignments && item.assignments.length > 0) {
//         const vip = item.assignments[0].vip;
//         if (vip) {
//           setSelectedVip(vip);
//         }
//       }
//     }

//     const rect = e.currentTarget.getBoundingClientRect();
//     setPopupPosition({
//       x: rect.left + rect.width / 2,
//       y: rect.top + window.scrollY,
//     });

//     setSelectedRow(item);
//   };

//   const handleSort = (field) => {
//     setSortConfig((prev) => {
//       const currentConfig = prev[currentView === "vip-view" ? "vip" : "guard"];
//       const newDirection =
//         currentConfig.field === field
//           ? currentConfig.direction === "asc"
//             ? "desc"
//             : "asc"
//           : "asc";

//       return {
//         ...prev,
//         [currentView === "vip-view" ? "vip" : "guard"]: {
//           field,
//           direction: newDirection,
//         },
//       };
//     });
//   };

//   const getSortIcon = (field) => {
//     const config =
//       currentView === "vip-view" ? sortConfig.vip : sortConfig.guard;
//     if (config.field !== field) return "fas fa-sort";
//     return config.direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
//   };

//   const handleFilterChange = (filterName, value) => {
//     setFilters((prev) => ({
//       ...prev,
//       [filterName]: value,
//     }));
//   };

//   const applyFilters = () => {
//     setCurrentPage(1);
//   };

//   const resetFilters = () => {
//     const today = new Date().toISOString().split("T")[0];
//     const thirtyDaysAgo = new Date();
//     thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

//     setFilters({
//       status: "all",
//       rank: "all",
//       vipStatus: "all",
//       guardStatus: "all",
//       experience: "all",
//       timesAssigned: "all",
//       dateFrom: thirtyDaysAgo.toISOString().split("T")[0],
//       dateTo: today,
//       search: "",
//     });
//     setCurrentPage(1);
//   };

//   const exportToPDF = () => {
//     const filteredItems = getFilteredItems();
//     const doc = new jsPDF();
//     const today = new Date().toISOString().split("T")[0];

//     // Header
//     doc.setFontSize(18);
//     doc.text(
//       `VIP Security Assignment Report - ${
//         currentView === "vip-view" ? "VIP View" : "Officers View"
//       }`,
//       14,
//       22
//     );
//     doc.setFontSize(11);
//     doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

//     // Stats
//     doc.setFontSize(12);
//     doc.text(`Total Assignments: ${stats.totalAssignments}`, 14, 40);
//     doc.text(`Active VIPs: ${stats.activeVips}`, 14, 46);
//     doc.text(`Active Officers: ${stats.activeGuards}`, 14, 52);

//     if (currentView === "vip-view") {
//       const vipMap = new Map();

//       filteredItems.forEach((item) => {
//         const vip = item.vip;
//         if (!vip) return;

//         if (!vipMap.has(vip.id)) {
//           vipMap.set(vip.id, {
//             name: vip.name || "Unknown VIP",
//             designation: vip.designation || "—",
//             status: vip.status || "Unknown",
//             guards: [],
//             guardRanks: [],
//             assignmentStatuses: [],
//             assignmentDates: [],
//             timesAssigned: 0,
//           });
//         }

//         const vipData = vipMap.get(vip.id);
//         item.officers.forEach((o) => {
//           vipData.guards.push(o.name || "Unknown Guard");
//           if (o.rank) vipData.guardRanks.push(o.rank);
//         });
//         vipData.assignmentStatuses.push(item.status);
//         vipData.assignmentDates.push(new Date(item.assignedAt));
//         vipData.timesAssigned += 1;
//       });

//       const headers = [
//         ["VIP Name", "Designation", "VIP Status", "Assigned Guards", "Officers Ranks", "Assignment Status", "Latest Date", "Total Times Assigned"],
//       ];

//       const tableData = [];
//       vipMap.forEach((vip) => {
//         const latestDate = vip.assignmentDates.length > 0
//           ? new Date(Math.max(...vip.assignmentDates.map(d => d.getTime()))).toLocaleDateString()
//           : "N/A";

//         const uniqueStatuses = [...new Set(vip.assignmentStatuses)].join(", ");
//         const uniqueRanks = [...new Set(vip.guardRanks)].join(", ");

//         tableData.push([
//           vip.name,
//           vip.designation,
//           vip.status,
//           vip.guards.join(", "),
//           uniqueRanks || "—",
//           uniqueStatuses,
//           latestDate,
//           vip.timesAssigned.toString(),
//         ]);
//       });

//       autoTable(doc, {
//         head: headers,
//         body: tableData,
//         startY: 60,
//         theme: "grid",
//         styles: { fontSize: 9, cellPadding: 3 },
//         headStyles: { fillColor: [25, 118, 210] },
//         columnStyles: {
//           0: { cellWidth: 25 },
//           1: { cellWidth: 25 },
//           2: { cellWidth: 20 },
//           3: { cellWidth: 35 },
//           4: { cellWidth: 20 },
//           5: { cellWidth: 25 },
//           6: { cellWidth: 20 },
//           7: { cellWidth: 20 },
//         },
//       });
//     } else {
//       const guardMap = new Map();

//       filteredItems.forEach((assignment) => {
//         assignment.officers.forEach((o) => {
//           if (!guardMap.has(o.id)) {
//             guardMap.set(o.id, {
//               name: o.name || "Unknown Guard",
//               rank: o.rank || "—",
//               status: o.status || "Unknown",
//               experience: o.experience || 0,
//               email: o.email || "—",
//               vipAssignments: [],
//             });
//           }

//           const guard = guardMap.get(o.id);
//           guard.vipAssignments.push({
//             vipName: assignment.vip?.name || "Unknown VIP",
//             vipDesignation: assignment.vip?.designation || "—",
//             assignmentStatus: assignment.status,
//             assignedAt: new Date(assignment.assignedAt),
//           });
//         });
//       });

//       const headers = [
//         ["Officer Name", "Rank", "Status", "Experience", "VIP Names", "VIP Designations", "Assignment Status", "Latest Assignment"],
//       ];

//       const tableData = [];
//       guardMap.forEach((guard) => {
//         const latestAssignment = guard.vipAssignments.length > 0
//           ? new Date(Math.max(...guard.vipAssignments.map(a => a.assignedAt.getTime()))).toLocaleDateString()
//           : "N/A";

//         const vipNames = [...new Set(guard.vipAssignments.map(a => a.vipName))].join(", ");
//         const vipDesignations = [...new Set(guard.vipAssignments.map(a => a.vipDesignation))].join(", ");
//         const assignmentStatuses = [...new Set(guard.vipAssignments.map(a => a.assignmentStatus))].join(", ");

//         tableData.push([
//           guard.name,
//           guard.rank,
//           guard.status,
//           `${guard.experience} years`,
//           vipNames || "—",
//           vipDesignations || "—",
//           assignmentStatuses,
//           latestAssignment,
//         ]);
//       });

//       autoTable(doc, {
//         head: headers,
//         body: tableData,
//         startY: 60,
//         theme: "grid",
//         styles: { fontSize: 9, cellPadding: 3 },
//         headStyles: { fillColor: [25, 118, 210] },
//         columnStyles: {
//           0: { cellWidth: 25 },
//           1: { cellWidth: 20 },
//           2: { cellWidth: 20 },
//           3: { cellWidth: 15 },
//           4: { cellWidth: 35 },
//           5: { cellWidth: 30 },
//           6: { cellWidth: 25 },
//           7: { cellWidth: 20 },
//         },
//       });
//     }

//     doc.save(`vip_security_${currentView}_${today}.pdf`);
//   };

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   // Generate pagination buttons
//   const renderPagination = () => {
//     if (totalPages <= 1) return null;

//     const buttons = [];
//     const maxVisiblePages = 5;
//     let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
//     let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

//     if (endPage - startPage + 1 < maxVisiblePages) {
//       startPage = Math.max(1, endPage - maxVisiblePages + 1);
//     }

//     // Previous button
//     buttons.push(
//       <button
//         key="prev"
//         onClick={() => handlePageChange(currentPage - 1)}
//         disabled={currentPage === 1}
//         style={styles.pageBtn}
//       >
//         <i className="fas fa-chevron-left"></i> Previous
//       </button>
//     );

//     // Page numbers
//     for (let i = startPage; i <= endPage; i++) {
//       buttons.push(
//         <button
//           key={i}
//           onClick={() => handlePageChange(i)}
//           style={{
//             ...styles.pageBtn,
//             ...(currentPage === i ? styles.activePage : {}),
//           }}
//         >
//           {i}
//         </button>
//       );
//     }

//     // Next button
//     buttons.push(
//       <button
//         key="next"
//         onClick={() => handlePageChange(currentPage + 1)}
//         disabled={currentPage === totalPages}
//         style={styles.pageBtn}
//       >
//         Next <i className="fas fa-chevron-right"></i>
//       </button>
//     );

//     return buttons;
//   };

//   return (
//     <div style={styles.container}>
//       <header style={styles.header}>
//         <div style={styles.headerContent}>
//           <h1 style={styles.headerTitle}>
//             <i
//               className="fas fa-shield-alt"
//               style={{ marginRight: "10px", color: "white" }}
//             ></i>
//             VIP Security Assignment Dashboard
//           </h1>
//           <div style={styles.stats}>
//             <div style={styles.statBox}>
//               <div style={styles.statValue}>{stats.totalAssignments}</div>
//               <div style={styles.statLabel}>Total Assignments</div>
//             </div>
//             <div style={styles.statBox}>
//               <div style={styles.statValue}>{stats.activeVips}</div>
//               <div style={styles.statLabel}>Active VIPs</div>
//             </div>
//             <div style={styles.statBox}>
//               <div style={styles.statValue}>{stats.activeGuards}</div>
//               <div style={styles.statLabel}>Active Guards</div>
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* View Toggle and Export */}
//       <div style={styles.viewToggle}>
//         <div style={styles.viewButtons}>
//           <button
//             style={{
//               ...styles.viewBtn,
//               ...(currentView === "vip-view" ? styles.activeViewBtn : {}),
//             }}
//             onClick={() => {
//               setCurrentView("vip-view");
//               setCurrentPage(1);
//             }}
//           >
//             <i className="fas fa-user-tie"></i> VIP View (VIP → Guards)
//           </button>
//           <button
//             style={{
//               ...styles.viewBtn,
//               ...(currentView === "guard-view" ? styles.activeViewBtn : {}),
//             }}
//             onClick={() => {
//               setCurrentView("guard-view");
//               setCurrentPage(1);
//             }}
//           >
//             <i className="fas fa-user-shield"></i> Officer View (Officer → VIPs)
//           </button>
//         </div>
//         <button style={styles.exportBtn} onClick={exportToPDF}>
//           <i className="fas fa-download"></i> Export PDF
//         </button>
//       </div>

//       {/* Search */}
//       <div style={styles.searchContainer}>
//         <div style={styles.group}>
//           <label style={styles.filterLabel}>
//             <i className="fas fa-search" /> Search
//           </label>
//           <input
//             type="text"
//             placeholder="Name / Email / Designation"
//             value={filters.search}
//             onChange={(e) => handleFilterChange("search", e.target.value)}
//             onKeyPress={(e) => e.key === "Enter" && applyFilters()}
//             style={styles.input}
//           />
//         </div>
//       </div>

//       {/* Main Content Area */}
//       <div style={styles.pageLayout}>
//         {/* Filters Sidebar */}
//         <div style={styles.sidebar}>
//           <div style={styles.sidebarHeader}>
//             <i className="fas fa-sliders-h"></i>
//             <span>Advanced Filters</span>
//           </div>

//           {/* Active Filters */}
//           {Object.values(filters).some(
//             (val) => val !== "all" && val !== ""
//           ) && (
//             <div style={styles.activeFilters}>
//               <strong>Active:</strong>
//               <div style={styles.activeFilterText}>
//                 {[
//                   filters.status !== "all" && `Status: ${filters.status}`,
//                   filters.rank !== "all" && `Rank: ${filters.rank}`,
//                   filters.vipStatus !== "all" && `VIP: ${filters.vipStatus}`,
//                   filters.guardStatus !== "all" &&
//                     `Officer: ${filters.guardStatus}`,
//                   filters.experience !== "all" && `Exp: ${filters.experience}`,
//                   filters.timesAssigned !== "all" &&
//                     `Assigned: ${filters.timesAssigned}`,
//                   filters.dateFrom &&
//                     `From: ${formatDateOnly(filters.dateFrom)}`,
//                   filters.dateTo && `To: ${formatDateOnly(filters.dateTo)}`,
//                   filters.search && `Search: "${filters.search}"`,
//                 ]
//                   .filter(Boolean)
//                   .join(" • ")}
//               </div>
//             </div>
//           )}

//           <div style={styles.sidebarBody}>
//             {/* Assignment Status */}
//             <div style={styles.group}>
//               <label style={styles.filterLabel}>
//                 <i className="fas fa-clipboard-check" /> Assignment Status
//               </label>
//               <select
//                 value={filters.status}
//                 onChange={(e) => handleFilterChange("status", e.target.value)}
//                 style={styles.select}
//               >
//                 <option value="all">All</option>
//                 <option value="Completed">Completed</option>
//                 <option value="Active Duty">Active Duty</option>
//                 <option value="Active">Active</option>
//                 <option value="replaced">Replaced</option>
//                 <option value="Pending">Pending</option>
//                 <option value="canceled">Canceled</option>
//                 <option value="Deviation">Deviation</option>
//               </select>
//             </div>

//             {/* Officer Rank */}
//             <div style={styles.group}>
//               <label style={styles.filterLabel}>
//                 <i className="fas fa-star" /> Officer Rank
//               </label>
//               <select
//                 value={filters.rank}
//                 onChange={(e) => handleFilterChange("rank", e.target.value)}
//                 style={styles.select}
//               >
//                 <option value="all">All</option>
//                 <option value="A Grade">A Grade</option>
//                 <option value="B Grade">B Grade</option>
//                 <option value="C Grade">C Grade</option>
//                 <option value="D Grade">D Grade</option>
//                 <option value="E Grade">E Grade</option>
//               </select>
//             </div>

//             {/* VIP Status */}
//             <div style={styles.group}>
//               <label style={styles.filterLabel}>
//                 <i className="fas fa-user" /> VIP Status
//               </label>
//               <select
//                 value={filters.vipStatus}
//                 onChange={(e) =>
//                   handleFilterChange("vipStatus", e.target.value)
//                 }
//                 style={styles.select}
//               >
//                 <option value="all">All</option>
//                 <option value="Active">Active</option>
//                 <option value="Inactive">Inactive</option>
//               </select>
//             </div>

//             {/* Officer Status */}
//             <div style={styles.group}>
//               <label style={styles.filterLabel}>
//                 <i className="fas fa-shield-alt" /> Officer Status
//               </label>
//               <select
//                 value={filters.guardStatus}
//                 onChange={(e) =>
//                   handleFilterChange("guardStatus", e.target.value)
//                 }
//                 style={styles.select}
//               >
//                 <option value="all">All</option>
//                 <option value="Active">Active</option>
//                 <option value="Inactive">Inactive</option>
//               </select>
//             </div>

//             {/* Experience */}
//             <div style={styles.group}>
//               <label style={styles.filterLabel}>
//                 <i className="fas fa-briefcase" /> Experience
//               </label>
//               <select
//                 value={filters.experience}
//                 onChange={(e) =>
//                   handleFilterChange("experience", e.target.value)
//                 }
//                 style={styles.select}
//               >
//                 <option value="all">All</option>
//                 <option value="0-5">0–5 Years</option>
//                 <option value="6-10">6–10 Years</option>
//                 <option value="11-15">11–15 Years</option>
//                 <option value="16-20">16–20 Years</option>
//                 <option value="20+">20+ Years</option>
//               </select>
//             </div>

//             {/* Date Range */}
//             <div style={styles.group}>
//               <label style={styles.filterLabel}>
//                 <i className="fas fa-calendar" /> Date From
//               </label>
//               <input
//                 type="date"
//                 value={filters.dateFrom}
//                 onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
//                 style={styles.input}
//               />
//             </div>

//             <div style={styles.group}>
//               <label style={styles.filterLabel}>
//                 <i className="fas fa-calendar" /> Date To
//               </label>
//               <input
//                 type="date"
//                 value={filters.dateTo}
//                 onChange={(e) => handleFilterChange("dateTo", e.target.value)}
//                 style={styles.input}
//               />
//             </div>
//           </div>

//           {/* Buttons */}
//           <div style={styles.sidebarFooter}>
//             <button style={styles.applyBtn} onClick={applyFilters}>
//               <i className="fas fa-check" /> Apply
//             </button>
//             <button style={styles.resetBtn} onClick={resetFilters}>
//               <i className="fas fa-redo" /> Reset
//             </button>
//           </div>
//         </div>

//         {/* Main Content */}
//         <div style={styles.mainContent}>
//           {currentView === "vip-view" && (
//             <div style={styles.contentArea}>
//               {loading ? (
//                 <div style={styles.noData}>Loading VIP data...</div>
//               ) : processedData.data.length === 0 ? (
//                 <div style={styles.emptyState}>
//                   <i
//                     className="fas fa-user-slash"
//                     style={{
//                       fontSize: "3rem",
//                       marginBottom: "15px",
//                       color: "#ccc",
//                     }}
//                   ></i>
//                   <h3>No VIPs Found</h3>
//                   <p>Try adjusting your filters to see results</p>
//                 </div>
//               ) : (
//                 <>
//                   <div style={styles.tableContainer}>
//                     <table style={styles.table}>
//                       <thead>
//                         <tr>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("name")}
//                           >
//                             VIP Name <i className={getSortIcon("name")}></i>
//                           </th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("designation")}
//                           >
//                             Designation{" "}
//                             <i className={getSortIcon("designation")}></i>
//                           </th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("status")}
//                           >
//                             Status <i className={getSortIcon("status")}></i>
//                           </th>
//                           <th style={styles.th}>Assigned Guards</th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("guard-rank")}
//                           >
//                             Officer Rank{" "}
//                             <i className={getSortIcon("guard-rank")}></i>
//                           </th>
//                           <th style={styles.th}>Assignment Status</th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("date")}
//                           >
//                             Latest Date{" "}
//                             <i className={getSortIcon("date")}></i>
//                           </th>
//                           <th style={styles.th}>Times Assigned</th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {processedData.data.map((vip) => {
//                           const statusCounts = vip.assignments.reduce(
//                             (acc, a) => {
//                               const s = a.status;
//                               if (s === "Completed" || s === "complete")
//                                 acc.completed++;
//                               else if (s === "Active Duty" || s === "Active")
//                                 acc.active++;
//                               else if (s === "Pending") acc.pending++;
//                               else if (s === "canceled" || s === "cancelled")
//                                 acc.canceled++;
//                               else if (s === "Deviation") acc.deviation++;
//                               else if (s === "replaced") acc.replaced++;
//                               return acc;
//                             },
//                             {
//                               completed: 0,
//                               active: 0,
//                               pending: 0,
//                               canceled: 0,
//                               deviation: 0,
//                               replaced: 0,
//                             }
//                           );

//                           return (
//                             <tr
//                               key={vip.id}
//                               style={styles.tableRow}
//                               onClick={(e) => {
//                                 handleRowClick(vip, e);
//                                 setExpandedVipId((prev) =>
//                                   prev === vip.id ? null : vip.id
//                                 );
//                               }}
//                             >
//                               <td style={styles.td}>
//                                 <div style={styles.vipName}>
//                                   {vip.name || "Unknown VIP"}
//                                 </div>
//                                 <div style={styles.emailText}>
//                                   {vip.email || "—"}
//                                 </div>
//                               </td>

//                               <td style={styles.td}>
//                                 {vip.designation || "—"}
//                               </td>

//                               <td style={styles.td}>
//                                 <span
//                                   className={getStatusClass(vip.status)}
//                                   style={styles.statusBadge}
//                                 >
//                                   {vip.status || "Unknown"}
//                                 </span>
//                               </td>

//                               <td style={styles.td}>
//                                 <strong>{vip.totalGuards}</strong> Guards
//                                 <div style={styles.smallText}>
//                                   {vip.assignments.length} assignment(s)
//                                 </div>
//                               </td>

//                               <td style={styles.td}>{vip.guardRanks}</td>

//                               <td style={styles.td}>
//                                 {statusCounts.completed > 0 && (
//                                   <span
//                                     className="status-completed"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.completed} Completed
//                                   </span>
//                                 )}
//                                 {statusCounts.active > 0 && (
//                                   <span
//                                     className="status-active"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.active} Active
//                                   </span>
//                                 )}
//                                 {statusCounts.pending > 0 && (
//                                   <span
//                                     className="status-pending"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.pending} Pending
//                                   </span>
//                                 )}
//                                 {statusCounts.canceled > 0 && (
//                                   <span
//                                     className="status-canceled"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.canceled} Canceled
//                                   </span>
//                                 )}
//                                 {statusCounts.deviation > 0 && (
//                                   <span
//                                     className="status-incident"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.deviation} Deviation
//                                   </span>
//                                 )}
//                                 {statusCounts.replaced > 0 && (
//                                   <span
//                                     className="status-replaced"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.replaced} Replaced
//                                   </span>
//                                 )}
//                               </td>

//                               <td style={styles.td}>
//                                 {vip.latestDate
//                                   ? vip.latestDate.toLocaleDateString()
//                                   : "N/A"}
//                               </td>

//                               <td style={styles.td}>
//                                 <strong>{vip.timesAssigned}</strong>
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Pagination */}
//                   <div style={styles.paginationContainer}>
//                     {renderPagination()}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}

//           {/* Guard View */}
//           {currentView === "guard-view" && (
//             <div style={styles.contentArea}>
//               {loading ? (
//                 <div style={styles.noData}>Loading Officers data...</div>
//               ) : processedData.data.length === 0 ? (
//                 <div style={styles.emptyState}>
//                   <i
//                     className="fas fa-user-shield"
//                     style={{
//                       fontSize: "3rem",
//                       marginBottom: "15px",
//                       color: "#ccc",
//                     }}
//                   ></i>
//                   <h3>No Guards Found</h3>
//                   <p>Try adjusting your filters to see results</p>
//                 </div>
//               ) : (
//                 <>
//                   <div style={styles.tableContainer}>
//                     <table style={styles.table}>
//                       <thead>
//                         <tr>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("name")}
//                           >
//                             Officer Name <i className={getSortIcon("name")}></i>
//                           </th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("rank")}
//                           >
//                             Rank <i className={getSortIcon("rank")}></i>
//                           </th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("status")}
//                           >
//                             Status <i className={getSortIcon("status")}></i>
//                           </th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("experience")}
//                           >
//                             Work Experience{" "}
//                             <i className={getSortIcon("experience")}></i>
//                           </th>
//                           <th style={styles.th}>Assigned VIPs</th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("vip-designation")}
//                           >
//                             VIP Designation{" "}
//                             <i className={getSortIcon("vip-designation")}></i>
//                           </th>
//                           <th style={styles.th}>Assignment Status</th>
//                           <th
//                             style={styles.th}
//                             onClick={() => handleSort("date")}
//                           >
//                             Latest Assignment{" "}
//                             <i className={getSortIcon("date")}></i>
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {processedData.data.map((guard) => {
//                           const statusCounts = guard.assignments.reduce(
//                             (acc, a) => {
//                               const s = a.assignmentStatus;
//                               if (s === "Completed" || s === "completed")
//                                 acc.completed++;
//                               else if (s === "Active Duty" || s === "Active")
//                                 acc.active++;
//                               else if (s === "Pending") acc.pending++;
//                               else if (s === "canceled") acc.canceled++;
//                               else if (s === "Deviation") acc.deviation++;
//                               else if (s === "Replaced") acc.replaced++;
//                               return acc;
//                             },
//                             {
//                               completed: 0,
//                               active: 0,
//                               pending: 0,
//                               canceled: 0,
//                               deviation: 0,
//                               replaced: 0,
//                             }
//                           );

//                           return (
//                             <tr
//                               key={guard.id}
//                               style={styles.tableRow}
//                               onClick={(e) => handleRowClick(guard, e)}
//                             >
//                               <td style={styles.td}>
//                                 <div style={styles.guardName}>{guard.name}</div>
//                                 <div style={styles.emailText}>
//                                   {guard.email}
//                                 </div>
//                               </td>
//                               <td style={styles.td}>
//                                 <span
//                                   className={getRankClass(guard.rank)}
//                                   style={styles.rankBadge}
//                                 >
//                                   {guard.rank}
//                                 </span>
//                               </td>
//                               <td style={styles.td}>
//                                 <span
//                                   className={getStatusClass(guard.status)}
//                                   style={styles.statusBadge}
//                                 >
//                                   {guard.status}
//                                 </span>
//                               </td>
//                               <td style={styles.td}>
//                                 {guard.experience} years
//                               </td>
//                               <td style={styles.td}>
//                                 <div>{guard.vipNames || "—"}</div>
//                                 <div style={styles.smallText}>
//                                   Total: {guard.assignments.length} assignment(s)
//                                 </div>
//                               </td>
//                               <td style={styles.td}>{guard.vipDesignations}</td>
//                               <td style={styles.td}>
//                                 {statusCounts.completed > 0 && (
//                                   <span
//                                     className="status-completed"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.completed} Completed
//                                   </span>
//                                 )}
//                                 {statusCounts.active > 0 && (
//                                   <span
//                                     className="status-active"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.active} Active
//                                   </span>
//                                 )}
//                                 {statusCounts.pending > 0 && (
//                                   <span
//                                     className="status-pending"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.pending} Pending
//                                   </span>
//                                 )}
//                                 {statusCounts.canceled > 0 && (
//                                   <span
//                                     className="status-canceled"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.canceled} Canceled
//                                   </span>
//                                 )}
//                                 {statusCounts.deviation > 0 && (
//                                   <span
//                                     className="status-incident"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.deviation} Deviation
//                                   </span>
//                                 )}
//                                 {statusCounts.replaced > 0 && (
//                                   <span
//                                     className="status-replaced"
//                                     style={styles.statusBadge}
//                                   >
//                                     {statusCounts.replaced} Replaced
//                                   </span>
//                                 )}
//                               </td>
//                               <td style={styles.td}>
//                                 {guard.latestAssignment
//                                   ? guard.latestAssignment.toLocaleDateString()
//                                   : "N/A"}
//                               </td>
//                             </tr>
//                           );
//                         })}
//                       </tbody>
//                     </table>
//                   </div>

//                   {/* Pagination */}
//                   <div style={styles.paginationContainer}>
//                     {renderPagination()}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}

//           {/* POPUP */}
//           {selectedRow && (
//             <div
//               style={{
//                 ...styles.popup,
//                 top: popupPosition.y - 20,
//                 left: popupPosition.x - 180,
//               }}
//             >
//               <div style={styles.popupTitle}>
//                 {currentView === "vip-view"
//                   ? "VIP Details"
//                   : "Officers Details"}
//               </div>

//               {currentView === "vip-view" ? (
//                 <>
//                   <div>
//                     <b>Name:</b> {selectedRow.name}
//                   </div>
//                   <div>
//                     <b>Email:</b> {selectedRow.email}
//                   </div>
//                   <div>
//                     <b>Designation:</b> {selectedRow.designation}
//                   </div>
//                   <div>
//                     <b>Status:</b> {selectedRow.status}
//                   </div>
//                   <div>
//                     <b>Total Assignments:</b> {selectedRow.timesAssigned || 0}
//                   </div>
//                   <div>
//                     <b>Total Guards Assigned:</b> {selectedRow.totalGuards || 0}
//                   </div>
//                 </>
//               ) : (
//                 <>
//                   <div>
//                     <b>Name:</b> {selectedRow.name}
//                   </div>
//                   <div>
//                     <b>Email:</b> {selectedRow.email}
//                   </div>
//                   <div>
//                     <b>Rank:</b> {selectedRow.rank}
//                   </div>
//                   <div>
//                     <b>Status:</b> {selectedRow.status}
//                   </div>
//                   <div>
//                     <b>Experience:</b> {selectedRow.experience} Years
//                   </div>
//                   <div>
//                     <b>Total VIP Assignments:</b>{" "}
//                     {selectedRow.assignments?.length || 0}
//                   </div>
//                 </>
//               )}

//               <button
//                 onClick={() => setSelectedRow(null)}
//                 style={styles.infoBtn}
//               >
//                 Close
//               </button>
//             </div>
//           )}

//           {/* Styles for badges */}
//           <style>{`
//         .status-completed { background-color: #e8f5e9; color: #2e7d32; padding: 4px 10px; border-radius: 20px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .status-active { background-color: #e3f2fd; color: #1565c0; padding: 4px 10px; border-radius: 20px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .status-pending { background-color: #fff3e0; color: #ef6c00; padding: 4px 10px; border-radius: 20px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .status-canceled { background-color: #ffebee; color: #c62828; padding: 4px 10px; border-radius: 20px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .status-incident { background-color: #fce4ec; color: #ad1457; padding: 4px 10px; border-radius: 20px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .status-replaced { background-color: #f3e5f5; color: #7b1fa2; padding: 4px 10px; border-radius: 20px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .rank-a { background-color: #ffebee; color: #c62828; padding: 4px 10px; border-radius: 4px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .rank-b { background-color: #f3e5f5; color: #7b1fa2; padding: 4px 10px; border-radius: 4px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .rank-c { background-color: #e8eaf6; color: #3949ab; padding: 4px 10px; border-radius: 4px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .rank-d { background-color: #e0f2f1; color: #00695c; padding: 4px 10px; border-radius: 4px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .rank-e { background-color: #fff3e0; color: #ef6c00; padding: 4px 10px; border-radius: 4px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin: 2px; }
//         .fas { margin-left: 5px; }
//       `}</style>
//         </div>
//       </div>
//     </div>
//   );
// }

// const styles = {
//   container: {
//     padding: "20px",
//     background:
//       "linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))",
//     color: "#333",
//     minHeight: "100vh",
//   },
//   header: {
//     background: "linear-gradient(135deg, #4e54c8, #8f94fb)",
//     color: "white",
//     padding: "25px 0",
//     borderRadius: "10px",
//     marginBottom: "30px",
//     boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
//   },
//   headerContent: {
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     padding: "0 30px",
//     flexWrap: "wrap",
//   },
//   headerTitle: {
//     fontSize: "2.2rem",
//     fontWeight: 600,
//     margin: 0,
//     color: "#fff",
//   },
//   stats: {
//     display: "flex",
//     gap: "20px",
//     flexWrap: "wrap",
//   },
//   statBox: {
//     backgroundColor: "rgba(255, 255, 255, 0.15)",
//     padding: "12px 20px",
//     borderRadius: "8px",
//     textAlign: "center",
//     minWidth: "150px",
//   },
//   statValue: {
//     fontSize: "1.8rem",
//     fontWeight: 700,
//   },
//   statLabel: {
//     fontSize: "0.9rem",
//     opacity: 0.9,
//     marginTop: "5px",
//   },
//   viewToggle: {
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     marginBottom: "25px",
//     backgroundColor: "white",
//     borderRadius: "10px",
//     padding: "10px 20px",
//     boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
//     flexWrap: "wrap",
//   },
//   viewButtons: {
//     display: "flex",
//     gap: "10px",
//     flexWrap: "wrap",
//   },
//   viewBtn: {
//     padding: "12px 30px",
//     border: "none",
//     backgroundColor: "transparent",
//     fontSize: "1rem",
//     fontWeight: 600,
//     color: "#666",
//     cursor: "pointer",
//     borderRadius: "6px",
//     transition: "all 0.3s ease",
//     display: "flex",
//     alignItems: "center",
//     gap: "8px",
//   },
//   activeViewBtn: {
//     backgroundColor: "#4e54c8",
//     color: "white",
//   },
//   exportBtn: {
//     backgroundColor: "#4CAF50",
//     color: "white",
//     padding: "10px 20px",
//     border: "none",
//     borderRadius: "6px",
//     cursor: "pointer",
//     fontWeight: 600,
//     display: "flex",
//     alignItems: "center",
//     gap: "8px",
//     transition: "background-color 0.3s ease",
//   },
//   searchContainer: {
//     backgroundColor: "white",
//     borderRadius: "10px",
//     padding: "20px",
//     marginBottom: "25px",
//     boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
//   },
//   pageLayout: {
//     display: "flex",
//     gap: "20px",
//     alignItems: "flex-start",
//   },
//   sidebar: {
//     width: "300px",
//     minWidth: "300px",
//     background: "white",
//     borderRadius: "10px",
//     padding: "20px",
//     boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
//     position: "sticky",
//     top: "20px",
//   },
//   sidebarHeader: {
//     fontSize: "1.2rem",
//     fontWeight: 600,
//     marginBottom: "20px",
//     display: "flex",
//     alignItems: "center",
//     gap: "10px",
//     color: "#1a237e",
//   },
//   activeFilters: {
//     backgroundColor: "#f1f8ff",
//     border: "1px solid #e3f2fd",
//     borderRadius: "8px",
//     padding: "12px",
//     marginBottom: "20px",
//     fontSize: "0.9rem",
//   },
//   activeFilterText: {
//     marginTop: "8px",
//     color: "#555",
//     fontSize: "0.85rem",
//   },
//   sidebarBody: {
//     maxHeight: "500px",
//     overflowY: "auto",
//     paddingRight: "5px",
//   },
//   group: {
//     marginBottom: "20px",
//   },
//   filterLabel: {
//     display: "flex",
//     alignItems: "center",
//     gap: "8px",
//     marginBottom: "8px",
//     fontWeight: 600,
//     color: "#555",
//     fontSize: "0.9rem",
//   },
//   select: {
//     width: "100%",
//     padding: "10px 12px",
//     border: "1px solid #ddd",
//     borderRadius: "6px",
//     fontSize: "0.95rem",
//     backgroundColor: "white",
//   },
//   input: {
//     width: "100%",
//     padding: "10px 12px",
//     border: "1px solid #ddd",
//     borderRadius: "6px",
//     fontSize: "0.95rem",
//     backgroundColor: "white",
//   },
//   sidebarFooter: {
//     display: "flex",
//     gap: "10px",
//     marginTop: "10px",
//   },
//   applyBtn: {
//     flex: 1,
//     padding: "12px",
//     backgroundColor: "#4e54c8",
//     color: "white",
//     border: "none",
//     borderRadius: "6px",
//     cursor: "pointer",
//     fontWeight: 600,
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     gap: "8px",
//   },
//   resetBtn: {
//     flex: 1,
//     padding: "12px",
//     backgroundColor: "#f5f5f5",
//     color: "#666",
//     border: "1px solid #ddd",
//     borderRadius: "6px",
//     cursor: "pointer",
//     fontWeight: 600,
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     gap: "8px",
//   },
//   mainContent: {
//     flex: 1,
//     minWidth: 0,
//   },
//   contentArea: {
//     animation: "fadeIn 0.5s ease",
//   },
//   tableContainer: {
//     backgroundColor: "white",
//     borderRadius: "10px",
//     overflow: "hidden",
//     boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
//     marginBottom: "30px",
//     overflowX: "auto",
//   },
//   table: {
//     width: "100%",
//     borderCollapse: "collapse",
//     minWidth: "1200px",
//   },
//   th: {
//     padding: "16px 12px",
//     textAlign: "left",
//     fontWeight: 600,
//     fontSize: "0.95rem",
//     cursor: "pointer",
//     userSelect: "none",
//     backgroundColor: "#1a237e",
//     color: "white",
//     borderBottom: "2px solid #ddd",
//   },
//   tableRow: {
//     borderBottom: "1px solid #f0f0f0",
//     transition: "background-color 0.2s ease",
//     cursor: "pointer",
//   },
//   tableRowHover: {
//     backgroundColor: "#f8f9fa",
//   },
//   td: {
//     padding: "14px 12px",
//     fontSize: "0.95rem",
//     verticalAlign: "top",
//   },
//   vipName: {
//     fontWeight: 600,
//     color: "#1a237e",
//   },
//   guardName: {
//     fontWeight: 600,
//     color: "#333",
//   },
//   emailText: {
//     fontSize: "0.85rem",
//     color: "#666",
//     marginTop: "2px",
//   },
//   smallText: {
//     fontSize: "0.85rem",
//     marginTop: "5px",
//     color: "#666",
//   },
//   statusBadge: {
//     padding: "5px 12px",
//     borderRadius: "20px",
//     fontSize: "0.85rem",
//     fontWeight: 600,
//     display: "inline-block",
//   },
//   rankBadge: {
//     padding: "4px 10px",
//     borderRadius: "4px",
//     fontSize: "0.85rem",
//     fontWeight: 600,
//     display: "inline-block",
//   },
//   emptyState: {
//     textAlign: "center",
//     padding: "60px 20px",
//     color: "#777",
//     backgroundColor: "white",
//     borderRadius: "10px",
//     boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
//   },
//   noData: {
//     textAlign: "center",
//     padding: "40px",
//     fontSize: "18px",
//     color: "#777",
//     backgroundColor: "white",
//     borderRadius: "10px",
//     boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
//   },
//   paginationContainer: {
//     display: "flex",
//     justifyContent: "center",
//     alignItems: "center",
//     gap: "10px",
//     marginTop: "20px",
//     flexWrap: "wrap",
//   },
//   pageBtn: {
//     padding: "8px 15px",
//     border: "1px solid #ddd",
//     backgroundColor: "white",
//     borderRadius: "5px",
//     cursor: "pointer",
//     transition: "all 0.2s ease",
//     display: "flex",
//     alignItems: "center",
//     gap: "5px",
//   },
//   activePage: {
//     backgroundColor: "#1a237e",
//     color: "white",
//     borderColor: "#1a237e",
//   },
//   popup: {
//     position: "absolute",
//     background: "#ffffff",
//     padding: "18px",
//     width: "340px",
//     borderRadius: "12px",
//     boxShadow: "0 8px 25px rgba(0,0,0,0.25)",
//     zIndex: 5000,
//   },
//   popupTitle: {
//     fontSize: "18px",
//     fontWeight: 700,
//     marginBottom: "10px",
//   },
//   infoBtn: {
//     background: "#1976d2",
//     padding: "7px 14px",
//     border: 0,
//     color: "#fff",
//     borderRadius: 8,
//     cursor: "pointer",
//     marginTop: "10px",
//   },
// };
// <style>{`
// @media (max-width: 1024px) {
//   .pageLayout {
//     flex-direction: column;
//   }
// }
// `}</style>;
